import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Target, Briefcase, FileText, CheckCircle, TrendingUp, Users, 
  Zap, Award, Lightbulb, BarChart3,
  Sparkles, Clock, Star, AlertCircle, HelpCircle,
  Layers, GitBranch, Package, Rocket, Presentation, BookOpen, Info, DollarSign, Brain, Shield
} from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import { piliers } from '../data/cadreReferentielBZ';
import { livrablesTangiblesParTactique } from '../data/livrablesTangiblesParTactique';
import { 
  parcoursStrategiques, 
  parcoursTactiques, 
  parcoursOperationnels, 
  parcoursMixtes
} from '../data/parcoursVentePRIsME';
import type { ParcoursVente } from '../data/parcoursVentePRIsME';
import { parcoursClientsParDuree } from '../data/parcoursClientsParDuree';
import type { ParcoursClientParDuree } from '../data/parcoursClientsParDuree';

interface GuideVentePRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Modal pour expliquer un niveau en détail
interface NiveauDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  niveau: 'strategique' | 'tactique' | 'operationnel';
}

const NiveauDetailModal: React.FC<NiveauDetailModalProps> = ({ isOpen, onClose, niveau }) => {
  if (!isOpen) return null;

  const niveauxData = {
    strategique: {
      titre: 'Niveau STRATÉGIQUE',
      couleur: 'indigo',
      icon: Target,
      description: 'La vision à long terme et la gouvernance TI',
      montant: {
        mensuel: '$10,000',
        annuel: '$120,000',
        pourcentage: '+567%',
        description: 'Premium - Transformation stratégique'
      },
      contenu: {
        definition: 'Le niveau stratégique, c\'est la VISION. C\'est répondre à la question : "Où veut-on être dans 3-5 ans ?" C\'est le niveau qui connecte les TI aux objectifs d\'affaires de l\'entreprise.',
        pourquoi: 'Pourquoi c\'est crucial pour le client ? Sans vision stratégique, les TI deviennent un centre de coûts au lieu d\'un avantage concurrentiel. Le client dépense de l\'argent sans savoir pourquoi, et il ne voit jamais le ROI.',
        commentVendre: 'Comment vendre ce niveau ? Dites au client : "Vous avez probablement déjà des TI qui fonctionnent (opérationnel). Mais avez-vous une VISION claire de où vous voulez être dans 3-5 ans ? Avez-vous un PLAN pour y arriver ? C\'est ça, le niveau stratégique. Sans ça, vous réagissez au lieu d\'anticiper."',
        exemples: [
          'Plan Directeur TI 3-5 ans',
          'Cadre de gouvernance TI',
          'Alignement stratégique TI / Business',
          'Budget stratégique multi-années',
          'Vision transformation digitale'
        ],
        objections: [
          {
            objection: '"On n\'a pas besoin de ça, on gère bien nos TI"',
            reponse: 'C\'est excellent que vous gérez bien ! Mais gérer, c\'est réactif. Le niveau stratégique, c\'est ANTICIPER. C\'est comme avoir un GPS : vous savez où vous allez avant de partir. Sans stratégie, vous conduisez sans destination.'
          },
          {
            objection: '"C\'est trop cher pour juste un document"',
            reponse: 'Ce n\'est pas "juste un document". C\'est votre FEUILLE DE ROUTE pour les 3-5 prochaines années. Sans ça, vous allez dépenser 3-4 fois plus en projets mal alignés. Le Plan Directeur vous fait ÉCONOMISER de l\'argent à long terme.'
          }
        ],
        valeur: 'La valeur du niveau stratégique ? Le client passe de "réactif" à "proactif". Il sait où il va, pourquoi il y va, et comment y arriver. C\'est ça qui transforme les TI d\'un centre de coûts en avantage concurrentiel.'
      }
    },
    tactique: {
      titre: 'Niveau TACTIQUE',
      couleur: 'blue',
      icon: Briefcase,
      description: 'Le plan d\'action concret sur 12-24 mois',
      montant: {
        mensuel: '$4,000',
        annuel: '$48,000',
        pourcentage: '+167%',
        description: 'Moyen - Planification structurée'
      },
      contenu: {
        definition: 'Le niveau tactique, c\'est le PLAN. C\'est répondre à la question : "Comment on y arrive ?" C\'est le pont entre la vision stratégique (où on veut être) et l\'opérationnel (ce qu\'on fait aujourd\'hui).',
        pourquoi: 'Pourquoi c\'est crucial pour le client ? Le client peut avoir une vision stratégique, mais sans plan tactique, c\'est comme avoir une destination sans carte routière. Il sait où il veut aller, mais il ne sait pas comment y arriver.',
        commentVendre: 'Comment vendre ce niveau ? Dites au client : "Vous avez une vision (stratégique) et vous avez des équipes qui travaillent (opérationnel). Mais avez-vous un PLAN clair pour les 12-24 prochains mois ? Une roadmap avec des priorités, des budgets, des jalons ? C\'est ça, le niveau tactique. C\'est votre GPS pour les 2 prochaines années."',
        exemples: [
          'Roadmap TI 12-24 mois',
          'ODS (Offre de Service)',
          'Budget annuel TI',
          'Cadre d\'objectifs et KPIs',
          'Plan de projets prioritaires'
        ],
        objections: [
          {
            objection: '"On a déjà un budget annuel"',
            reponse: 'Excellent ! Mais votre budget est-il aligné avec une roadmap structurée ? Est-ce que chaque dollar dépensé contribue à votre vision stratégique ? Le niveau tactique, c\'est s\'assurer que votre budget sert votre stratégie, pas juste payer les factures.'
          },
          {
            objection: '"On fait déjà des projets"',
            reponse: 'Faire des projets, c\'est bien. Mais sont-ils PRIORISÉS selon votre stratégie ? Le niveau tactique, c\'est s\'assurer que vous faites les BONS projets dans le BON ordre. Sinon, vous dépensez de l\'argent sur des projets qui ne servent pas votre vision.'
          }
        ],
        valeur: 'La valeur du niveau tactique ? Le client passe de "on fait des projets" à "on fait les BONS projets dans le BON ordre". Il maximise son ROI, évite les projets inutiles, et avance vers sa vision stratégique de manière structurée.'
      }
    },
    operationnel: {
      titre: 'Niveau OPÉRATIONNEL',
      couleur: 'emerald',
      icon: Zap,
      description: 'Le quotidien : support, incidents, outils',
      montant: {
        mensuel: '$1,500',
        annuel: '$18,000',
        pourcentage: 'Base',
        description: 'Base - Support réactif'
      },
      contenu: {
        definition: 'Le niveau opérationnel, c\'est le QUOTIDIEN. C\'est répondre à la question : "Qu\'est-ce qu\'on fait aujourd\'hui ?" C\'est le support TI, la gestion des incidents, les outils, les processus ITIL.',
        pourquoi: 'Pourquoi c\'est crucial pour le client ? C\'est la BASE. Sans niveau opérationnel solide, rien ne fonctionne. Mais attention : avoir SEULEMENT l\'opérationnel, c\'est être toujours en mode "réaction". Vous éteignez des feux au lieu de prévenir les problèmes.',
        commentVendre: 'Comment vendre ce niveau ? Dites au client : "Vous avez probablement déjà un niveau opérationnel solide. C\'est votre force historique. Mais êtes-vous toujours en mode réaction ? Vous éteignez des feux au lieu de prévenir les problèmes ? C\'est parce qu\'il vous manque les niveaux stratégique et tactique. L\'opérationnel, c\'est les fondations. Mais une maison sans murs ni toit, ça ne protège personne."',
        exemples: [
          'Support TI quotidien',
          'Gestion des incidents',
          'Gestion des changements',
          'Monitoring et outils',
          'Processus ITIL opérationnels'
        ],
        objections: [
          {
            objection: '"On gère déjà bien nos TI au quotidien"',
            reponse: 'C\'est excellent ! Vous avez les fondations solides. Mais une maison avec seulement des fondations, c\'est incomplet. Vous avez besoin des murs (tactique) et du toit (stratégique) pour être vraiment protégé. Le niveau opérationnel, c\'est votre force. Maintenant, construisons dessus.'
          },
          {
            objection: '"On n\'a pas besoin de plus, ça fonctionne"',
            reponse: 'Ça fonctionne MAINTENANT. Mais qu\'en sera-t-il dans 2 ans ? Dans 5 ans ? Sans vision stratégique et plan tactique, vous allez continuer à réagir aux problèmes au lieu de les prévenir. C\'est comme conduire sans GPS : vous arrivez peut-être à destination, mais vous perdez du temps et de l\'essence.'
          }
        ],
        valeur: 'La valeur du niveau opérationnel ? C\'est la BASE. Mais combiné avec les niveaux stratégique et tactique, il devient PROACTIF au lieu de réactif. Le client prévient les problèmes au lieu de les éteindre.'
      }
    }
  };

  const data = niveauxData[niveau];
  const Icon = data.icon;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className={`relative w-[90vw] max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col`}
        >
          {/* Header */}
          <div className={`p-6 ${
            niveau === 'strategique' ? 'bg-gradient-to-r from-indigo-600 to-purple-600' :
            niveau === 'tactique' ? 'bg-gradient-to-r from-blue-600 to-cyan-600' :
            'bg-gradient-to-r from-emerald-600 to-teal-600'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`bg-white/20 backdrop-blur-xl p-4 rounded-xl`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white">{data.titre}</h2>
                  <p className="text-white/90 text-sm mt-1">{data.description}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Montant financier */}
              <div className={`rounded-xl p-6 text-white shadow-lg ${
                niveau === 'strategique' ? 'bg-gradient-to-r from-indigo-600 to-purple-600' :
                niveau === 'tactique' ? 'bg-gradient-to-r from-blue-600 to-cyan-600' :
                'bg-gradient-to-r from-emerald-600 to-teal-600'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/90 mb-1">Valeur typique du contrat</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-black">{data.montant.mensuel}</span>
                      <span className="text-lg text-white/80">/mois</span>
                    </div>
                    <p className="text-sm text-white/80 mt-1">≈ {data.montant.annuel}/an</p>
                  </div>
                  <div className="text-right">
                    <div className="inline-block px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <p className="text-xs text-white/90 mb-1">{data.montant.pourcentage}</p>
                      <p className="text-xs font-semibold">{data.montant.description}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Définition */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Info className={`w-5 h-5 ${
                    niveau === 'strategique' ? 'text-indigo-600' :
                    niveau === 'tactique' ? 'text-blue-600' :
                    'text-emerald-600'
                  }`} />
                  Qu'est-ce que c'est ?
                </h3>
                <p className="text-gray-700 leading-relaxed">{data.contenu.definition}</p>
              </div>

              {/* Pourquoi */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <HelpCircle className={`w-5 h-5 ${
                    niveau === 'strategique' ? 'text-indigo-600' :
                    niveau === 'tactique' ? 'text-blue-600' :
                    'text-emerald-600'
                  }`} />
                  Pourquoi c'est crucial pour le client ?
                </h3>
                <p className="text-gray-700 leading-relaxed">{data.contenu.pourquoi}</p>
              </div>

              {/* Comment vendre */}
              <div className={`rounded-xl p-6 border shadow-sm ${
                niveau === 'strategique' ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200' :
                niveau === 'tactique' ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200' :
                'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200'
              }`}>
                <h3 className={`text-lg font-bold text-gray-900 mb-3 flex items-center gap-2`}>
                  <Presentation className={`w-5 h-5 ${
                    niveau === 'strategique' ? 'text-indigo-600' :
                    niveau === 'tactique' ? 'text-blue-600' :
                    'text-emerald-600'
                  }`} />
                  Comment vendre ce niveau ?
                </h3>
                <p className="text-gray-700 leading-relaxed italic">"{data.contenu.commentVendre}"</p>
              </div>

              {/* Exemples */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className={`text-lg font-bold text-gray-900 mb-4 flex items-center gap-2`}>
                  <FileText className={`w-5 h-5 ${
                    niveau === 'strategique' ? 'text-indigo-600' :
                    niveau === 'tactique' ? 'text-blue-600' :
                    'text-emerald-600'
                  }`} />
                  Exemples de livrables
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.contenu.exemples.map((exemple, idx) => (
                    <div key={idx} className={`flex items-center gap-2 p-3 rounded-lg border ${
                      niveau === 'strategique' ? 'bg-indigo-50 border-indigo-200' :
                      niveau === 'tactique' ? 'bg-blue-50 border-blue-200' :
                      'bg-emerald-50 border-emerald-200'
                    }`}>
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 ${
                        niveau === 'strategique' ? 'text-indigo-600' :
                        niveau === 'tactique' ? 'text-blue-600' :
                        'text-emerald-600'
                      }`} />
                      <span className="text-sm text-gray-700">{exemple}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Objections */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  Réponses aux objections courantes
                </h3>
                <div className="space-y-4">
                  {data.contenu.objections.map((obj, idx) => (
                    <div key={idx} className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <p className="font-semibold text-orange-900 mb-2 text-sm">Objection : "{obj.objection}"</p>
                      <p className="text-gray-700 text-sm leading-relaxed italic">Réponse : "{obj.reponse}"</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Valeur */}
              <div className={`rounded-xl p-6 text-white shadow-lg ${
                niveau === 'strategique' ? 'bg-gradient-to-r from-indigo-600 to-purple-600' :
                niveau === 'tactique' ? 'bg-gradient-to-r from-blue-600 to-cyan-600' :
                'bg-gradient-to-r from-emerald-600 to-teal-600'
              }`}>
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  La valeur pour le client
                </h3>
                <p className="text-white/90 leading-relaxed">{data.contenu.valeur}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Modal pour expliquer un parcours en détail
interface ParcoursDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  parcours: ParcoursVente | ParcoursClientParDuree | null;
}

const ParcoursDetailModal: React.FC<ParcoursDetailModalProps> = ({ isOpen, onClose, parcours }) => {
  if (!isOpen || !parcours) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className={`relative w-[90vw] max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col`}
        >
          {/* Header */}
          <div className={`p-6 ${
            parcours.niveau === 'strategique' ? 'bg-gradient-to-r from-indigo-600 to-purple-600' :
            parcours.niveau === 'tactique' ? 'bg-gradient-to-r from-blue-600 to-cyan-600' :
            parcours.niveau === 'operationnel' ? 'bg-gradient-to-r from-emerald-600 to-teal-600' :
            'bg-gradient-to-r from-purple-600 to-pink-600'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur-xl p-4 rounded-xl">
                  {parcours.icon && <parcours.icon className="w-8 h-8 text-white" />}
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white">{parcours.nom}</h2>
                  <p className="text-white/90 text-sm mt-1">{parcours.description}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Montant et durée */}
              <div className={`rounded-xl p-6 text-white shadow-lg ${
                parcours.niveau === 'strategique' ? 'bg-gradient-to-r from-indigo-600 to-purple-600' :
                parcours.niveau === 'tactique' ? 'bg-gradient-to-r from-blue-600 to-cyan-600' :
                parcours.niveau === 'operationnel' ? 'bg-gradient-to-r from-emerald-600 to-teal-600' :
                'bg-gradient-to-r from-purple-600 to-pink-600'
              }`}>
                <div>
                  <p className="text-sm text-white/90 mb-3">Prix selon la taille de l'entreprise</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-xs text-white/80 mb-1">25-30 employés</p>
                      <p className="text-lg font-black">{parcours.montant['25-30']}</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-xs text-white/80 mb-1">30-50 employés</p>
                      <p className="text-lg font-black">{parcours.montant['30-50']}</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-xs text-white/80 mb-1">50-75 employés</p>
                      <p className="text-lg font-black">{parcours.montant['50-75']}</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-xs text-white/80 mb-1">75-125 employés</p>
                      <p className="text-lg font-black">{parcours.montant['75-125']}</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-xs text-white/80 mb-1">125-250 employés</p>
                      <p className="text-lg font-black">{parcours.montant['125-250']}</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-xs text-white/80 mb-1">250+ employés</p>
                      <p className="text-lg font-black">{parcours.montant['250+']}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/20">
                    <p className="text-xs text-white/80">Durée recommandée : <strong>{parcours.duree}</strong></p>
                  </div>
                  <p className="text-xs text-white/80 mt-3 italic">
                    💡 Les prix sont indicatifs et peuvent varier selon la complexité, le secteur d'activité, et les besoins spécifiques.
                  </p>
                </div>
              </div>

              {/* Note sur les prix */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-sm text-gray-700">
                  <strong>💡 Note importante :</strong> Les prix affichés sont adaptés selon la taille de l'entreprise (nombre d'employés). 
                  Nos clients se situent principalement dans les catégories <strong>25-30, 30-50, 50-75, et 75-125 employés</strong>. 
                  Les entreprises de <strong>125-250 employés</strong> sont moins fréquentes, et <strong>250+</strong> sont très rares. 
                  Les prix peuvent varier selon la complexité, le secteur d'activité, et les besoins spécifiques.
                </p>
              </div>

              {/* Livrables inclus */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-indigo-600" />
                  Ce qui est inclus dans ce parcours
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {parcours.livrables.map((livrable: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{livrable}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comment vendre */}
              {parcours.commentVendre && (
                <div className={`rounded-xl p-6 border shadow-sm ${
                  parcours.niveau === 'strategique' ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200' :
                  parcours.niveau === 'tactique' ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200' :
                  parcours.niveau === 'operationnel' ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200' :
                  'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
                }`}>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Presentation className={`w-5 h-5 ${
                      parcours.niveau === 'strategique' ? 'text-indigo-600' :
                      parcours.niveau === 'tactique' ? 'text-blue-600' :
                      parcours.niveau === 'operationnel' ? 'text-emerald-600' :
                      'text-purple-600'
                    }`} />
                    Comment vendre ce parcours ? (Script prêt à l'emploi)
                  </h3>
                  <p className="text-gray-700 leading-relaxed italic">"{parcours.commentVendre}"</p>
                </div>
              )}

              {/* Pour qui */}
              {parcours.pourQui && (
                <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Pour quel type de client ?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{parcours.pourQui}</p>
                </div>
              )}

              {/* Objections */}
              {parcours.objections && parcours.objections.length > 0 && (
                <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    Réponses aux objections courantes
                  </h3>
                  <div className="space-y-4">
                    {parcours.objections.map((obj: any, idx: number) => (
                      <div key={idx} className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                        <p className="font-semibold text-orange-900 mb-2 text-sm">Objection : "{obj.objection}"</p>
                        <p className="text-gray-700 text-sm leading-relaxed italic">Réponse : "{obj.reponse}"</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Valeur */}
              {parcours.valeur && (
                <div className={`rounded-xl p-6 text-white shadow-lg ${
                  parcours.niveau === 'strategique' ? 'bg-gradient-to-r from-indigo-600 to-purple-600' :
                  parcours.niveau === 'tactique' ? 'bg-gradient-to-r from-blue-600 to-cyan-600' :
                  parcours.niveau === 'operationnel' ? 'bg-gradient-to-r from-emerald-600 to-teal-600' :
                  'bg-gradient-to-r from-purple-600 to-pink-600'
                }`}>
                  <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Star className="w-5 h-5" />
                    La valeur pour le client
                  </h3>
                  <p className="text-white/90 leading-relaxed">{parcours.valeur}</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const GuideVentePRIsMEModal: React.FC<GuideVentePRIsMEModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('niveaux');
  const [niveauDetailModal, setNiveauDetailModal] = useState<{
    isOpen: boolean;
    niveau: 'strategique' | 'tactique' | 'operationnel' | null;
  }>({
    isOpen: false,
    niveau: null
  });
  const [parcoursDetailModal, setParcoursDetailModal] = useState<{
    isOpen: boolean;
    parcours: any;
  }>({
    isOpen: false,
    parcours: null
  });
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['guide_vente_prisme_last_ai_analysis'] ?? null) : null;

  if (!isOpen) return null;

  // Données pour les livrables tangibles
  const livrables = livrablesTangiblesParTactique;
  const livrablesParNiveau = {
    strategique: livrables.filter(l => 
      l.profondeur === 'Humaine' || 
      (l.type === 'Document' && (l.livrableNom.toLowerCase().includes('plan directeur') || l.livrableNom.toLowerCase().includes('gouvernance'))) ||
      l.type === 'Présentation'
    ),
    tactique: livrables.filter(l => 
      l.profondeur === 'Procédurale' || 
      l.type === 'Rapport' || 
      l.type === 'Matrice' ||
      (l.type === 'Document' && (l.livrableNom.toLowerCase().includes('roadmap') || l.livrableNom.toLowerCase().includes('ods') || l.livrableNom.toLowerCase().includes('budget')))
    ),
    operationnel: livrables.filter(l => 
      l.profondeur === 'Technologique' || 
      l.type === 'Outil' || 
      l.type === 'Dashboard' ||
      l.type === 'Processus'
    )
  };

  // Onglets
  const tabs = [
    { id: 'niveaux', label: 'Les 3 Niveaux', icon: Layers },
    { id: 'parcours', label: 'Parcours Clients', icon: GitBranch },
    { id: 'packages', label: 'Packages PRIsME', icon: Package },
    { id: 'approches', label: '2 Approches ODS', icon: Rocket },
    { id: 'livrables', label: 'Livrables Tangibles', icon: FileText },
    { id: 'avantages', label: 'Avantages Vendeurs', icon: Star },
    { id: 'scripts', label: 'Scripts de Vente', icon: Presentation },
    { id: 'comparaison', label: 'Avant/Après PRIsME', icon: BarChart3 }
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-[98vw] h-[96vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4">
            <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm cursor-pointer"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            <div className="relative flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-xl p-3 rounded-xl">
                <Rocket className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <h1 className="text-2xl font-black text-white">Guide de Vente PRIsME</h1>
                  {/* Badge AI - Disponible dans AI Assistant */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                    title="Disponible dans AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <p className="text-white/90 text-sm">Solution d'Affaire - Votre guide complet pour vendre comme un pro</p>
                {lastAnalysisDate && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-white/80">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="relative mt-4 flex gap-1 overflow-x-auto pb-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-white text-indigo-600 shadow-lg'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
            {/* ONGLET 1: LES 3 NIVEAUX */}
            {activeTab === 'niveaux' && (
              <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                    <Layers className="w-7 h-7 text-indigo-600" />
                    Les 3 Niveaux de la Solution d'Affaire
                  </h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Pour vendre efficacement PRIsME, vous devez comprendre les <strong>3 niveaux</strong> de profondeur dans les TI. 
                    C'est comme une pyramide : chaque niveau construit sur le précédent. Le client doit comprendre cette progression.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-gray-700">
                      <strong>💡 Pourquoi c'est important pour VOUS (le vendeur) :</strong> Quand vous comprenez les 3 niveaux, 
                      vous pouvez identifier rapidement où se trouve le client. Vous savez quoi proposer, dans quel ordre, 
                      et vous pouvez expliquer la valeur de chaque niveau de manière claire. C'est votre <strong>arme secrète</strong> pour 
                      transformer une simple vente en partenariat stratégique.
                    </p>
                  </div>

                  {/* Pyramide visuelle */}
                  <div className="flex flex-col items-center mb-8">
                    <div className="w-full max-w-md space-y-2">
                      {/* Niveau Stratégique */}
                      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg p-4 text-white shadow-lg relative">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3 flex-1">
                            <Target className="w-6 h-6 flex-shrink-0" />
                            <div className="flex-1">
                              <h3 className="text-lg font-bold">1. STRATÉGIQUE</h3>
                              <p className="text-sm text-indigo-100 mt-1">
                                Vision 3-5 ans, Plan Directeur TI, alignement business, gouvernance, budget stratégique
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setNiveauDetailModal({ isOpen: true, niveau: 'strategique' })}
                            className="ml-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all flex-shrink-0"
                            title="Guide détaillé - Niveau Stratégique"
                          >
                            <BookOpen className="w-3 h-3" />
                            Guide
                          </button>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="bg-white/20 px-2 py-1 rounded text-xs">Plan Directeur</span>
                          <span className="bg-white/20 px-2 py-1 rounded text-xs">Gouvernance</span>
                          <span className="bg-white/20 px-2 py-1 rounded text-xs">Stratégie</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-white/20">
                          <p className="text-xs text-indigo-100 italic">
                            💡 <strong>Message vente :</strong> "Avez-vous une vision claire de où vous voulez être dans 3-5 ans ? 
                            Sans stratégie, vous réagissez au lieu d'anticiper."
                          </p>
                        </div>
                      </div>

                      {/* Niveau Tactique */}
                      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg p-4 text-white shadow-lg ml-8 relative">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3 flex-1">
                            <Briefcase className="w-6 h-6 flex-shrink-0" />
                            <div className="flex-1">
                              <h3 className="text-lg font-bold">2. TACTIQUE</h3>
                              <p className="text-sm text-blue-100 mt-1">
                                Roadmap 12-24 mois, projets prioritaires, budgets annuels, ODS, cadres d'objectifs
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setNiveauDetailModal({ isOpen: true, niveau: 'tactique' })}
                            className="ml-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all flex-shrink-0"
                            title="Guide détaillé - Niveau Tactique"
                          >
                            <BookOpen className="w-3 h-3" />
                            Guide
                          </button>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="bg-white/20 px-2 py-1 rounded text-xs">Roadmap</span>
                          <span className="bg-white/20 px-2 py-1 rounded text-xs">ODS</span>
                          <span className="bg-white/20 px-2 py-1 rounded text-xs">Budget</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-white/20">
                          <p className="text-xs text-blue-100 italic">
                            💡 <strong>Message vente :</strong> "Vous avez une vision ? Parfait ! Mais avez-vous un PLAN concret 
                            pour les 12-24 prochains mois ? C'est votre GPS pour y arriver."
                          </p>
                        </div>
                      </div>

                      {/* Niveau Opérationnel */}
                      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg p-4 text-white shadow-lg ml-16 relative">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3 flex-1">
                            <Zap className="w-6 h-6 flex-shrink-0" />
                            <div className="flex-1">
                              <h3 className="text-lg font-bold">3. OPÉRATIONNEL</h3>
                              <p className="text-sm text-emerald-100 mt-1">
                                Support quotidien, incidents, changements, monitoring, outils, processus ITIL
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setNiveauDetailModal({ isOpen: true, niveau: 'operationnel' })}
                            className="ml-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all flex-shrink-0"
                            title="Guide détaillé - Niveau Opérationnel"
                          >
                            <BookOpen className="w-3 h-3" />
                            Guide
                          </button>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="bg-white/20 px-2 py-1 rounded text-xs">Support</span>
                          <span className="bg-white/20 px-2 py-1 rounded text-xs">Incidents</span>
                          <span className="bg-white/20 px-2 py-1 rounded text-xs">Outils</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-white/20">
                          <p className="text-xs text-emerald-100 italic">
                            💡 <strong>Message vente :</strong> "Vous gérez bien vos TI au quotidien ? Excellent ! 
                            Mais êtes-vous toujours en mode réaction ? C'est parce qu'il vous manque les niveaux stratégique et tactique."
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section : La Progression Financière ($$$) */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-300 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-green-500 p-3 rounded-xl">
                        <DollarSign className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-black text-gray-900">La Progression Financière : Plus on monte, plus ça paye</h3>
                        <p className="text-sm text-gray-600">Montants typiques par niveau (valeurs indicatives)</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      {/* Niveau Opérationnel */}
                      <div className="bg-white rounded-lg p-5 border-2 border-emerald-300 shadow-md">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Zap className="w-5 h-5 text-emerald-600" />
                            <h4 className="font-bold text-emerald-600 text-sm">OPÉRATIONNEL</h4>
                          </div>
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded font-semibold">Base</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-black text-emerald-600">$1,500</span>
                            <span className="text-sm text-gray-500">/mois</span>
                          </div>
                          <p className="text-xs text-gray-600">Support TI réactif</p>
                          <p className="text-xs text-gray-600">Gestion incidents</p>
                          <p className="text-xs text-gray-600">Monitoring basique</p>
                        </div>
                        <div className="mt-3 pt-3 border-t border-emerald-200">
                          <p className="text-xs text-emerald-700 font-semibold">≈ $18,000/an</p>
                        </div>
                      </div>

                      {/* Niveau Tactique */}
                      <div className="bg-white rounded-lg p-5 border-2 border-blue-400 shadow-lg relative">
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          +167%
                        </div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-blue-600" />
                            <h4 className="font-bold text-blue-600 text-sm">TACTIQUE</h4>
                          </div>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold">Moyen</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-black text-blue-600">$4,000</span>
                            <span className="text-sm text-gray-500">/mois</span>
                          </div>
                          <p className="text-xs text-gray-600">+ Roadmap 12-24 mois</p>
                          <p className="text-xs text-gray-600">+ ODS structurée</p>
                          <p className="text-xs text-gray-600">+ Budget planifié</p>
                          <p className="text-xs text-gray-600">+ Cadre KPI</p>
                        </div>
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <p className="text-xs text-blue-700 font-semibold">≈ $48,000/an</p>
                        </div>
                      </div>

                      {/* Niveau Stratégique */}
                      <div className="bg-white rounded-lg p-5 border-2 border-indigo-500 shadow-xl relative">
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                          +567%
                        </div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Target className="w-5 h-5 text-indigo-600" />
                            <h4 className="font-bold text-indigo-600 text-sm">STRATÉGIQUE</h4>
                          </div>
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded font-semibold">Premium</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-black text-indigo-600">$10,000</span>
                            <span className="text-sm text-gray-500">/mois</span>
                          </div>
                          <p className="text-xs text-gray-600">+ Plan Directeur 3-5 ans</p>
                          <p className="text-xs text-gray-600">+ Gouvernance TI</p>
                          <p className="text-xs text-gray-600">+ Vision stratégique</p>
                          <p className="text-xs text-gray-600">+ Budget multi-années</p>
                          <p className="text-xs text-gray-600">+ Alignement business</p>
                        </div>
                        <div className="mt-3 pt-3 border-t border-indigo-200">
                          <p className="text-xs text-indigo-700 font-semibold">≈ $120,000/an</p>
                        </div>
                      </div>
                    </div>

                    {/* Message pour les vendeurs */}
                    <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border border-green-300">
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-bold text-gray-900 mb-2">
                            💰 Message clé pour vos clients :
                          </p>
                          <p className="text-sm text-gray-700 leading-relaxed italic mb-2">
                            "Monsieur le client, regardez la progression : l'opérationnel, c'est $1,500/mois. 
                            Le tactique, c'est $4,000/mois. Le stratégique, c'est $10,000/mois. 
                            <strong>Plus on monte en niveau, plus la valeur augmente, et plus votre ROI est élevé.</strong>"
                          </p>
                          <p className="text-xs text-gray-600">
                            <strong>Pourquoi ça augmente ?</strong> Parce que chaque niveau apporte une valeur exponentielle : 
                            l'opérationnel répare, le tactique planifie, le stratégique transforme votre entreprise. 
                            C'est ça, la différence entre un coût et un investissement.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Tableau comparatif */}
                    <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-3 text-sm flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-green-600" />
                        Comparaison Annuelle
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b-2 border-gray-300">
                              <th className="text-left py-2 px-3 font-bold text-gray-700">Niveau</th>
                              <th className="text-right py-2 px-3 font-bold text-gray-700">Mensuel</th>
                              <th className="text-right py-2 px-3 font-bold text-gray-700">Annuel</th>
                              <th className="text-right py-2 px-3 font-bold text-gray-700">ROI Estimé</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-gray-200">
                              <td className="py-2 px-3">
                                <span className="inline-flex items-center gap-1">
                                  <Zap className="w-3 h-3 text-emerald-600" />
                                  <span className="font-semibold text-emerald-600">Opérationnel</span>
                                </span>
                              </td>
                              <td className="text-right py-2 px-3 font-mono">$1,500</td>
                              <td className="text-right py-2 px-3 font-mono font-bold">$18,000</td>
                              <td className="text-right py-2 px-3 text-gray-600">Réactif</td>
                            </tr>
                            <tr className="border-b border-gray-200 bg-blue-50">
                              <td className="py-2 px-3">
                                <span className="inline-flex items-center gap-1">
                                  <Briefcase className="w-3 h-3 text-blue-600" />
                                  <span className="font-semibold text-blue-600">Tactique</span>
                                </span>
                              </td>
                              <td className="text-right py-2 px-3 font-mono">$4,000</td>
                              <td className="text-right py-2 px-3 font-mono font-bold">$48,000</td>
                              <td className="text-right py-2 px-3 text-gray-600">Planifié</td>
                            </tr>
                            <tr className="bg-indigo-50">
                              <td className="py-2 px-3">
                                <span className="inline-flex items-center gap-1">
                                  <Target className="w-3 h-3 text-indigo-600" />
                                  <span className="font-semibold text-indigo-600">Stratégique</span>
                                </span>
                              </td>
                              <td className="text-right py-2 px-3 font-mono">$10,000</td>
                              <td className="text-right py-2 px-3 font-mono font-bold">$120,000</td>
                              <td className="text-right py-2 px-3 text-gray-600">Transformation</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-gray-500 mt-3 italic">
                        💡 <strong>Note :</strong> Ces montants sont indicatifs et varient selon la taille de l'entreprise, 
                        la complexité des besoins, et la durée du contrat. Utilisez-les comme référence pour expliquer la progression.
                      </p>
                    </div>
                  </div>

                  {/* Section : Pourquoi les 3 niveaux existent */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-purple-600" />
                      Pourquoi les 3 niveaux existent ? (L'angle psychologique)
                    </h3>
                    <div className="space-y-4 text-gray-700">
                      <p className="text-sm leading-relaxed">
                        <strong>🧠 Comprendre le cerveau du client :</strong> Les clients pensent souvent que "avoir des TI qui fonctionnent" 
                        (opérationnel) suffit. C'est normal, c'est ce qu'ils voient au quotidien. Mais vous, en tant que vendeur expert, 
                        vous savez que c'est incomplet. Le problème ? Le client ne le sait pas encore.
                      </p>
                      <p className="text-sm leading-relaxed">
                        <strong>💭 La résistance au changement :</strong> Quand vous proposez un Plan Directeur (stratégique), le client pense 
                        souvent "Pourquoi j'aurais besoin de ça ? Mes TI fonctionnent déjà !" C'est là que vous devez être pédagogue. 
                        Expliquez que l'opérationnel, c'est comme avoir une voiture qui roule. Mais sans GPS (tactique) et sans destination (stratégique), 
                        vous allez nulle part efficacement.
                      </p>
                      <p className="text-sm leading-relaxed">
                        <strong>🎯 Votre rôle de vendeur :</strong> Vous n'êtes pas juste un vendeur de services. Vous êtes un <strong>éducateur</strong>. 
                        Vous aidez le client à comprendre ce qu'il ne sait pas encore qu'il a besoin. C'est ça, la vraie vente consultative.
                      </p>
                    </div>
                  </div>

                  {/* Section : Comment identifier où se trouve le client */}
                  <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-600" />
                      Comment identifier où se trouve le client ? (Questions de découverte)
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="font-bold text-indigo-600 mb-2 text-sm">Questions pour identifier le niveau Stratégique :</h4>
                        <ul className="text-sm text-gray-700 space-y-1 ml-4">
                          <li>• "Avez-vous une vision TI sur 3-5 ans ?"</li>
                          <li>• "Vos TI sont-elles alignées avec vos objectifs d'affaires ?"</li>
                          <li>• "Avez-vous un Plan Directeur TI ?"</li>
                          <li>• "Comment décidez-vous quels projets TI prioriser ?"</li>
                        </ul>
                        <p className="text-xs text-gray-600 mt-2 italic">
                          Si le client répond "Non" ou "On fait au cas par cas" → Il manque le niveau stratégique
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="font-bold text-blue-600 mb-2 text-sm">Questions pour identifier le niveau Tactique :</h4>
                        <ul className="text-sm text-gray-700 space-y-1 ml-4">
                          <li>• "Avez-vous une roadmap TI pour les 12-24 prochains mois ?"</li>
                          <li>• "Comment planifiez-vous vos budgets TI annuels ?"</li>
                          <li>• "Avez-vous un ODS (Offre de Service) avec votre fournisseur TI ?"</li>
                          <li>• "Comment suivez-vous l'avancement de vos projets TI ?"</li>
                        </ul>
                        <p className="text-xs text-gray-600 mt-2 italic">
                          Si le client répond "On fait au fur et à mesure" → Il manque le niveau tactique
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="font-bold text-emerald-600 mb-2 text-sm">Questions pour identifier le niveau Opérationnel :</h4>
                        <ul className="text-sm text-gray-700 space-y-1 ml-4">
                          <li>• "Avez-vous un support TI réactif ?"</li>
                          <li>• "Comment gérez-vous les incidents TI ?"</li>
                          <li>• "Avez-vous des outils de monitoring ?"</li>
                          <li>• "Vos processus TI sont-ils documentés ?"</li>
                        </ul>
                        <p className="text-xs text-gray-600 mt-2 italic">
                          Si le client répond "Oui" → Il a probablement déjà le niveau opérationnel (c'est souvent le cas)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Explication pour la vente */}
                  <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-indigo-600" />
                      Comment vendre avec les 3 niveaux ? (Scripts prêts à l'emploi)
                    </h3>
                    <div className="space-y-4 text-gray-700">
                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <p className="text-sm font-semibold text-indigo-600 mb-2">🎯 Message clé (à adapter selon le client) :</p>
                        <p className="text-sm leading-relaxed italic">
                          "Monsieur/Madame le client, vous avez probablement déjà un niveau opérationnel solide. 
                          C'est votre force historique. Mais pour devenir vraiment excellent, il faut construire les niveaux stratégique et tactique. 
                          C'est comme construire une maison : vous avez les fondations (opérationnel), maintenant il faut les murs (tactique) et le toit (stratégique)."
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <p className="text-sm font-semibold text-indigo-600 mb-2">💡 Pourquoi c'est important (explication pédagogique) :</p>
                        <p className="text-sm leading-relaxed">
                          Les clients qui n'ont que l'opérationnel sont toujours en mode <strong>"réaction"</strong>. 
                          Ils éteignent des feux au lieu de prévenir les problèmes. Avec PRIsME, on leur donne la vision stratégique 
                          (où aller) ET le plan tactique (comment y arriver). C'est ça, la vraie valeur : passer de réactif à proactif.
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <p className="text-sm font-semibold text-indigo-600 mb-2">📊 Livrables par niveau :</p>
                        <div className="grid grid-cols-3 gap-4 mt-3">
                          <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                            <h4 className="font-bold text-indigo-600 mb-2 text-xs">Stratégique</h4>
                            <ul className="text-xs space-y-1 text-gray-600">
                              <li>• Plan Directeur TI</li>
                              <li>• Cadre Gouvernance</li>
                              <li>• Vision 3-5 ans</li>
                              <li>• Budget stratégique</li>
                            </ul>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                            <h4 className="font-bold text-blue-600 mb-2 text-xs">Tactique</h4>
                            <ul className="text-xs space-y-1 text-gray-600">
                              <li>• Roadmap 12-24 mois</li>
                              <li>• ODS (Offre de Service)</li>
                              <li>• Budget annuel</li>
                              <li>• Cadre KPI</li>
                            </ul>
                          </div>
                          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                            <h4 className="font-bold text-emerald-600 mb-2 text-xs">Opérationnel</h4>
                            <ul className="text-xs space-y-1 text-gray-600">
                              <li>• Support TI</li>
                              <li>• Gestion incidents</li>
                              <li>• Monitoring</li>
                              <li>• Outils quotidiens</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section : La métaphore de la maison */}
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-6 border border-orange-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Rocket className="w-5 h-5 text-orange-600" />
                      La Métaphore de la Maison (Votre outil de vente)
                    </h3>
                    <div className="space-y-4 text-gray-700">
                      <p className="text-sm leading-relaxed">
                        <strong>🏠 Utilisez cette métaphore avec vos clients :</strong>
                      </p>
                      <div className="bg-white rounded-lg p-4 border border-orange-200">
                        <p className="text-sm leading-relaxed italic mb-3">
                          "Imaginez que vos TI sont une maison. Vous avez probablement déjà des <strong>fondations solides</strong> 
                          (niveau opérationnel : support, incidents, outils). C'est excellent ! Mais une maison avec seulement des fondations, 
                          ça ne protège personne de la pluie.
                        </p>
                        <p className="text-sm leading-relaxed italic mb-3">
                          Vous avez besoin de <strong>murs</strong> (niveau tactique : roadmap, ODS, budget annuel) pour structurer 
                          et organiser. Et vous avez besoin d'un <strong>toit</strong> (niveau stratégique : Plan Directeur, vision, gouvernance) 
                          pour vous protéger à long terme et vous donner une direction claire.
                        </p>
                        <p className="text-sm leading-relaxed italic">
                          PRIsME, c'est construire votre maison TI complète. Vous gardez vos fondations (votre force opérationnelle), 
                          et on ajoute les murs et le toit pour que vous soyez vraiment protégé et orienté."
                        </p>
                      </div>
                      <p className="text-xs text-orange-600 font-semibold">
                        ✨ <strong>Pourquoi cette métaphore fonctionne :</strong> Elle est visuelle, simple, et le client comprend immédiatement 
                        pourquoi il a besoin des 3 niveaux. C'est votre argument de vente le plus puissant.
                      </p>
                    </div>
                  </div>

                  {/* Section : L'ordre d'importance */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-gray-600" />
                      L'Ordre d'Importance : Pourquoi commencer par le Stratégique ?
                    </h3>
                    <div className="space-y-3 text-gray-700">
                      <p className="text-sm leading-relaxed">
                        <strong>⚠️ Erreur courante des clients :</strong> Beaucoup de clients veulent commencer par l'opérationnel 
                        ("Améliorons d'abord notre support"). C'est compréhensible, c'est ce qu'ils voient au quotidien. 
                        Mais c'est comme construire une maison en commençant par le toit.
                      </p>
                      <p className="text-sm leading-relaxed">
                        <strong>✅ L'ordre correct :</strong>
                      </p>
                      <ol className="list-decimal list-inside space-y-2 ml-4 text-sm">
                        <li><strong>Stratégique d'abord</strong> : Définir la vision et la destination (où on veut être)</li>
                        <li><strong>Tactique ensuite</strong> : Créer le plan pour y arriver (comment y arriver)</li>
                        <li><strong>Opérationnel enfin</strong> : Exécuter le plan au quotidien (ce qu'on fait aujourd'hui)</li>
                      </ol>
                      <p className="text-sm leading-relaxed mt-3">
                        <strong>💡 Comment expliquer ça au client :</strong> "Monsieur le client, si on améliore votre support (opérationnel) 
                        sans savoir vers où vous voulez aller (stratégique), on va améliorer quelque chose qui ne sert peut-être pas votre vision. 
                        C'est comme améliorer votre moteur de voiture sans savoir où vous allez. On commence par la destination, ensuite le GPS, 
                        ensuite on optimise le moteur."
                      </p>
                    </div>
                  </div>

                  {/* Section : Alignement avec les Standards Internationaux */}
                  <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-lg p-6 border-2 border-blue-200 mt-6">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-4 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-700">
                          🛡️ Alignement avec les Standards Internationaux
                        </h3>
                        <p className="text-gray-700 font-medium">PRIsME s'appuie sur les meilleures pratiques reconnues mondialement</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      {/* ITIL 4 */}
                      <div className="bg-white rounded-lg p-5 border-2 border-blue-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Shield className="w-5 h-5 text-blue-600" />
                          </div>
                          <h4 className="font-bold text-gray-800">ITIL 4 - Service Management</h4>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">
                          PRIsME intègre les principes ITIL 4 pour la gestion de la relation client et la livraison de services :
                        </p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• <strong>Value streams</strong> : Optimisation des processus de vente</li>
                          <li>• <strong>Guiding principles</strong> : Focus sur la valeur, collaboration, amélioration continue</li>
                          <li>• <strong>Service level management</strong> : Gestion des attentes et SLA commerciaux</li>
                          <li>• <strong>Service catalogue</strong> : Catalogue structuré de livrables tangibles</li>
                        </ul>
                      </div>

                      {/* COBIT 2019 */}
                      <div className="bg-white rounded-lg p-5 border-2 border-indigo-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            <Shield className="w-5 h-5 text-indigo-600" />
                          </div>
                          <h4 className="font-bold text-gray-800">COBIT 2019 - Governance</h4>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">
                          Alignement avec COBIT 2019 pour la gouvernance commerciale et la gestion des risques :
                        </p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• <strong>EDM (Evaluate, Direct, Monitor)</strong> : Cadre de gouvernance commerciale</li>
                          <li>• <strong>Risk management</strong> : Identification et gestion des risques commerciaux</li>
                          <li>• <strong>Performance management</strong> : KPIs et métriques alignées avec les objectifs</li>
                          <li>• <strong>Information & data</strong> : Gestion de l'information client et commerciale</li>
                        </ul>
                      </div>

                      {/* PMBOK / Agile */}
                      <div className="bg-white rounded-lg p-5 border-2 border-purple-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Shield className="w-5 h-5 text-purple-600" />
                          </div>
                          <h4 className="font-bold text-gray-800">PMBOK / Agile - Project Management</h4>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">
                          Intégration des meilleures pratiques de gestion de projet pour les cycles commerciaux :
                        </p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• <strong>Stakeholder management</strong> : Gestion des parties prenantes commerciales</li>
                          <li>• <strong>Risk management</strong> : Identification proactive des risques de vente</li>
                          <li>• <strong>Agile methodology</strong> : Adaptation rapide aux changements du marché</li>
                          <li>• <strong>Communication management</strong> : Communication efficace avec les clients</li>
                        </ul>
                      </div>

                      {/* ISO 9001 / NIST */}
                      <div className="bg-white rounded-lg p-5 border-2 border-green-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <Award className="w-5 h-5 text-green-600" />
                          </div>
                          <h4 className="font-bold text-gray-800">ISO 9001 / NIST CSF 2.0</h4>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">
                          Principes d'amélioration continue et de qualité appliqués à l'excellence commerciale :
                        </p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• <strong>Process approach</strong> : Processus commerciaux standardisés et optimisés</li>
                          <li>• <strong>Continuous improvement</strong> : Amélioration continue des performances</li>
                          <li>• <strong>Customer focus</strong> : Orientation client au cœur de toutes les actions</li>
                          <li>• <strong>Evidence-based decisions</strong> : Décisions basées sur les données et KPIs</li>
                        </ul>
                      </div>
                    </div>

                    {/* Message de synthèse */}
                    <div className="mt-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-lg p-6 text-white shadow-xl">
                      <p className="text-center text-lg font-bold mb-2">
                        🌟 Un Guide de Vente Normé et Professionnel
                      </p>
                      <p className="text-center text-white/90">
                        Ce guide ne réinvente pas la roue - il s'appuie sur les <strong>standards reconnus mondialement</strong> (ITIL 4, COBIT 2019, PMBOK, ISO 9001, NIST CSF 2.0) pour garantir une approche <strong>professionnelle, structurée et alignée</strong> avec les meilleures pratiques de l'industrie. Chaque livrable, chaque parcours, chaque script est aligné avec ces standards, permettant une présentation <strong>crédible et rassurante</strong> pour vos clients.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 2: PARCOURS CLIENTS */}
            {activeTab === 'parcours' && (
              <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                    <GitBranch className="w-7 h-7 text-purple-600" />
                    Parcours Clients - Par Durée de Contrat
                  </h2>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Nos parcours sont structurés selon la <strong>durée d'engagement</strong> du client. 
                    Chaque durée offre une <strong>profondeur différente</strong> et plusieurs choix de parcours adaptés. 
                    Plus la durée est longue, plus on peut suivre l'évolution et la progression de la posture TI.
                  </p>

                  {/* SECTION 1: COURT TERME (< 3 mois) */}
                  <div className="mb-8">
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-4 mb-4">
                      <h3 className="text-xl font-black text-white flex items-center gap-2">
                        <Clock className="w-6 h-6" />
                        COURT TERME - Contrats &lt; 3 mois
                      </h3>
                      <p className="text-orange-100 text-sm mt-1">
                        Parcours ciblés pour des besoins spécifiques ou des projets ponctuels. Flexibilité maximale, résultats rapides.
                      </p>
                    </div>

                    <div className="space-y-4">
                      {/* Parcours Court Terme 1 */}
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-black text-gray-900 mb-2">🔍 "L'Éclaireur" - Diagnostic Express</h3>
                            <p className="text-gray-600 text-sm">Comprendre rapidement la posture TI actuelle et identifier les priorités</p>
                          </div>
                          <span className="bg-emerald-600 text-white px-4 py-2 rounded-full font-bold text-sm">1-2 mois</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="w-4 h-4 text-indigo-600" />
                              <span className="text-xs font-semibold text-gray-700">Stratégique</span>
                            </div>
                            <p className="text-xs text-gray-600">Diagnostic TI complet</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Briefcase className="w-4 h-4 text-blue-600" />
                              <span className="text-xs font-semibold text-gray-700">Tactique</span>
                            </div>
                            <p className="text-xs text-gray-600">Plan d'action priorisé</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Zap className="w-4 h-4 text-emerald-600" />
                              <span className="text-xs font-semibold text-gray-700">Opérationnel</span>
                            </div>
                            <p className="text-xs text-gray-600">Recommandations immédiates</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-emerald-300 mb-3">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Livrables :</strong> Diagnostic TI complet, Rapport d'évaluation avec spider chart AS-IS, 
                            Plan d'action priorisé (quick wins), Recommandations opérationnelles immédiates
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Profondeur :</strong> Évaluation complète de la posture actuelle, identification des gaps critiques, 
                            recommandations actionnables à court terme
                          </p>
                        </div>
                        <button
                          onClick={() => setParcoursDetailModal({ isOpen: true, parcours: parcoursClientsParDuree.find(p => p.id === 'ct1') || null })}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                        >
                          <BookOpen className="w-4 h-4" />
                          Guide Complet
                        </button>
                      </div>

                      {/* Parcours Court Terme 2 */}
                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-black text-gray-900 mb-2">🛡️ "Le Gardien" - Audit Cybersécurité</h3>
                            <p className="text-gray-600 text-sm">Évaluation complète de la posture cybersécurité selon NIST 2.0</p>
                          </div>
                          <span className="bg-blue-600 text-white px-4 py-2 rounded-full font-bold text-sm">1-2 mois</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="w-4 h-4 text-indigo-600" />
                              <span className="text-xs font-semibold text-gray-700">Stratégique</span>
                            </div>
                            <p className="text-xs text-gray-600">Plan Cybersécurité NIST</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Briefcase className="w-4 h-4 text-blue-600" />
                              <span className="text-xs font-semibold text-gray-700">Tactique</span>
                            </div>
                            <p className="text-xs text-gray-600">Audit sécurité + Plan remédiation</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Zap className="w-4 h-4 text-emerald-600" />
                              <span className="text-xs font-semibold text-gray-700">Opérationnel</span>
                            </div>
                            <p className="text-xs text-gray-600">Recommandations immédiates sécurité</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-blue-300 mb-3">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Livrables :</strong> Audit cybersécurité NIST 2.0 complet, Plan de remédiation priorisé, 
                            Politiques sécurité, Rapport de conformité, Recommandations immédiates
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Profondeur :</strong> Évaluation des 6 fonctions NIST (Gouvernance, Identifier, Protéger, Détecter, Répondre, Récupérer), 
                            identification des risques critiques, plan d'action structuré
                          </p>
                        </div>
                        <button
                          onClick={() => setParcoursDetailModal({ isOpen: true, parcours: parcoursClientsParDuree.find(p => p.id === 'ct2') || null })}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                        >
                          <BookOpen className="w-4 h-4" />
                          Guide Complet
                        </button>
                      </div>

                      {/* Parcours Court Terme 3 */}
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-black text-gray-900 mb-2">⚡ "L'Accélérateur" - Quick Win TI</h3>
                            <p className="text-gray-600 text-sm">Interventions ciblées pour des résultats rapides et mesurables</p>
                          </div>
                          <span className="bg-purple-600 text-white px-4 py-2 rounded-full font-bold text-sm">1-3 mois</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="w-4 h-4 text-indigo-600" />
                              <span className="text-xs font-semibold text-gray-700">Stratégique</span>
                            </div>
                            <p className="text-xs text-gray-600">Priorisation stratégique</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Briefcase className="w-4 h-4 text-blue-600" />
                              <span className="text-xs font-semibold text-gray-700">Tactique</span>
                            </div>
                            <p className="text-xs text-gray-600">Plan d'action 3-6 mois</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Zap className="w-4 h-4 text-emerald-600" />
                              <span className="text-xs font-semibold text-gray-700">Opérationnel</span>
                            </div>
                            <p className="text-xs text-gray-600">Interventions immédiates</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-purple-300 mb-3">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Livrables :</strong> Analyse des quick wins, Plan d'action priorisé, 
                            Mise en œuvre d'interventions ciblées, Rapport de résultats
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Profondeur :</strong> Focus sur des améliorations rapides et mesurables, 
                            démonstration de valeur immédiate, préparation pour engagement plus long terme
                          </p>
                        </div>
                        <button
                          onClick={() => setParcoursDetailModal({ isOpen: true, parcours: parcoursClientsParDuree.find(p => p.id === 'ct3') || null })}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                        >
                          <BookOpen className="w-4 h-4" />
                          Guide Complet
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 2: TACTIQUE (1 an) */}
                  <div className="mb-8">
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl p-4 mb-4">
                      <h3 className="text-xl font-black text-white flex items-center gap-2">
                        <Briefcase className="w-6 h-6" />
                        TACTIQUE - Contrats 1 an
                      </h3>
                      <p className="text-blue-100 text-sm mt-1">
                        Parcours avec Roadmap annuelle et prise en charge pour suivre l'évolution de la posture TI. 
                        Révision trimestrielle, ajustements, progression mesurable.
                      </p>
                    </div>

                    <div className="space-y-4">

                      {/* Parcours Tactique 1 */}
                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-black text-gray-900 mb-2">🗺️ "Le Cartographe" - Roadmap & Évolution</h3>
                            <p className="text-gray-600 text-sm">Roadmap annuelle complète avec suivi trimestriel de l'évolution de la posture TI</p>
                          </div>
                          <span className="bg-blue-600 text-white px-4 py-2 rounded-full font-bold text-sm">1 an</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="w-4 h-4 text-indigo-600" />
                              <span className="text-xs font-semibold text-gray-700">Stratégique</span>
                            </div>
                            <p className="text-xs text-gray-600">Vision 3-5 ans + Plan Directeur</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Briefcase className="w-4 h-4 text-blue-600" />
                              <span className="text-xs font-semibold text-gray-700">Tactique</span>
                            </div>
                            <p className="text-xs text-gray-600">Roadmap 12 mois + Révisions trimestrielles</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Zap className="w-4 h-4 text-emerald-600" />
                              <span className="text-xs font-semibold text-gray-700">Opérationnel</span>
                            </div>
                            <p className="text-xs text-gray-600">Suivi progression + Ajustements</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-blue-300">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Livrables :</strong> Plan Directeur TI, Roadmap annuelle détaillée, Budget stratégique, 
                            Cadre de gouvernance, Matrice RACI, <strong>4 révisions trimestrielles</strong> avec évolution de la posture TI
                          </p>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Profondeur :</strong> Vision stratégique complète, planification tactique détaillée, 
                            <strong>suivi continu de l'évolution</strong> de la posture TI avec spider charts comparatifs (début vs trimestre), 
                            ajustements basés sur les résultats, progression mesurable
                          </p>
                          <p className="text-xs text-indigo-600 font-semibold">
                            ✨ <strong>Valeur ajoutée :</strong> Le client voit son évolution trimestre par trimestre, 
                            avec des ajustements proactifs et une progression mesurable de sa posture TI
                          </p>
                        </div>
                        <button
                          onClick={() => setParcoursDetailModal({ isOpen: true, parcours: parcoursClientsParDuree.find(p => p.id === 't1') || null })}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors mt-3"
                        >
                          <BookOpen className="w-4 h-4" />
                          Guide Complet
                        </button>
                      </div>

                      {/* Parcours Tactique 2 */}
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-black text-gray-900 mb-2">🎯 "Le Stratège" - ODS & Transformation</h3>
                            <p className="text-gray-600 text-sm">Offre de Service complète avec transformation structurée sur 1 an</p>
                          </div>
                          <span className="bg-indigo-600 text-white px-4 py-2 rounded-full font-bold text-sm">1 an</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="w-4 h-4 text-indigo-600" />
                              <span className="text-xs font-semibold text-gray-700">Stratégique</span>
                            </div>
                            <p className="text-xs text-gray-600">ODS avec 3 offres adaptées</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Briefcase className="w-4 h-4 text-blue-600" />
                              <span className="text-xs font-semibold text-gray-700">Tactique</span>
                            </div>
                            <p className="text-xs text-gray-600">Plan de transformation 12 mois</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Zap className="w-4 h-4 text-emerald-600" />
                              <span className="text-xs font-semibold text-gray-700">Opérationnel</span>
                            </div>
                            <p className="text-xs text-gray-600">Mise en œuvre + Suivi</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-indigo-300">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Livrables :</strong> ODS complète (3 offres), Plan de transformation annuel, 
                            Budget détaillé, <strong>Suivi mensuel de progression</strong>, Révisions trimestrielles, 
                            Rapport d'évolution annuel
                          </p>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Profondeur :</strong> Transformation structurée selon le Cadre BZ, 
                            <strong>suivi continu de la progression</strong> de chaque pilier, ajustements basés sur les résultats, 
                            démonstration de valeur à chaque trimestre
                          </p>
                          <p className="text-xs text-indigo-600 font-semibold">
                            ✨ <strong>Valeur ajoutée :</strong> Le client voit sa transformation en temps réel, 
                            avec des jalons clairs et une progression mesurable vers ses objectifs
                          </p>
                        </div>
                        <button
                          onClick={() => setParcoursDetailModal({ isOpen: true, parcours: parcoursClientsParDuree.find(p => p.id === 't2') || null })}
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors mt-3"
                        >
                          <BookOpen className="w-4 h-4" />
                          Guide Complet
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 3: STRATÉGIQUE (2-3 ans) */}
                  <div className="mb-8">
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl p-4 mb-4">
                      <h3 className="text-xl font-black text-white flex items-center gap-2">
                        <Target className="w-6 h-6" />
                        STRATÉGIQUE - Contrats 2-3 ans
                      </h3>
                      <p className="text-indigo-100 text-sm mt-1">
                        Parcours avec vision long terme, révision annuelle, avancement continu de la posture TI. 
                        Évolution annuelle mesurée, progression sur plusieurs années, transformation durable.
                      </p>
                    </div>

                    <div className="space-y-4">

                      {/* Parcours Stratégique 1 */}
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-black text-gray-900 mb-2">🏆 "Le Visionnaire" - Transformation Durable</h3>
                            <p className="text-gray-600 text-sm">Vision long terme avec révision annuelle et suivi continu de l'évolution de la posture TI</p>
                          </div>
                          <span className="bg-purple-600 text-white px-4 py-2 rounded-full font-bold text-sm">2-3 ans</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="w-4 h-4 text-indigo-600" />
                              <span className="text-xs font-semibold text-gray-700">Stratégique</span>
                            </div>
                            <p className="text-xs text-gray-600">Plan Directeur 3-5 ans + Vision</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Briefcase className="w-4 h-4 text-blue-600" />
                              <span className="text-xs font-semibold text-gray-700">Tactique</span>
                            </div>
                            <p className="text-xs text-gray-600">Roadmap annuelle + Révisions annuelles</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Zap className="w-4 h-4 text-emerald-600" />
                              <span className="text-xs font-semibold text-gray-700">Opérationnel</span>
                            </div>
                            <p className="text-xs text-gray-600">Suivi continu + Évolution mesurée</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-purple-300">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Livrables :</strong> Plan Directeur TI 3-5 ans, Roadmap annuelle (révisée chaque année), 
                            Budget stratégique multi-années, Cadre de gouvernance, <strong>Rapport d'évolution annuel</strong> avec progression, 
                            <strong>Révisions annuelles</strong> avec ajustements stratégiques
                          </p>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Profondeur :</strong> Vision stratégique complète sur 3-5 ans, planification tactique annuelle, 
                            <strong>suivi continu de l'avancement</strong> de la posture TI avec comparaison année sur année, 
                            <strong>évolution mesurée</strong> de chaque pilier, ajustements stratégiques basés sur les résultats, 
                            progression visible sur plusieurs années
                          </p>
                          <p className="text-xs text-purple-600 font-semibold">
                            ✨ <strong>Valeur ajoutée :</strong> Le client voit sa transformation sur plusieurs années, 
                            avec une progression mesurable, des ajustements stratégiques annuels, et une vision claire de son évolution
                          </p>
                        </div>
                        <button
                          onClick={() => setParcoursDetailModal({ isOpen: true, parcours: parcoursClientsParDuree.find(p => p.id === 's1') || null })}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors mt-3"
                        >
                          <BookOpen className="w-4 h-4" />
                          Guide Complet
                        </button>
                      </div>

                      {/* Parcours Stratégique 2 */}
                      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-black text-gray-900 mb-2">🚀 "L'Excellence" - Partenariat Stratégique</h3>
                            <p className="text-gray-600 text-sm">Partenariat complet avec transformation continue et excellence opérationnelle</p>
                          </div>
                          <span className="bg-indigo-600 text-white px-4 py-2 rounded-full font-bold text-sm">2-3 ans</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="w-4 h-4 text-indigo-600" />
                              <span className="text-xs font-semibold text-gray-700">Stratégique</span>
                            </div>
                            <p className="text-xs text-gray-600">Vision + Gouvernance + Innovation</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Briefcase className="w-4 h-4 text-blue-600" />
                              <span className="text-xs font-semibold text-gray-700">Tactique</span>
                            </div>
                            <p className="text-xs text-gray-600">ODS + Roadmap + Budget annuel</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Zap className="w-4 h-4 text-emerald-600" />
                              <span className="text-xs font-semibold text-gray-700">Opérationnel</span>
                            </div>
                            <p className="text-xs text-gray-600">Support complet + Portail PRIsME</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-indigo-300">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Livrables :</strong> Tous les documents stratégiques + tactiques + opérationnels, 
                            Comité TI structuré, Portail PRIsME Client, Support complet, 
                            <strong>Rapport d'évolution annuel</strong> avec progression détaillée, 
                            <strong>Révisions annuelles</strong> avec ajustements, Plan d'amélioration continue
                          </p>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Profondeur :</strong> Transformation complète selon le Cadre BZ, 
                            <strong>suivi continu de l'avancement</strong> de tous les piliers, 
                            <strong>évolution mesurée</strong> année sur année avec spider charts comparatifs, 
                            ajustements stratégiques et tactiques basés sur les résultats, 
                            progression visible et mesurable sur 2-3 ans
                          </p>
                          <p className="text-xs text-indigo-600 font-semibold">
                            ✨ <strong>Valeur ajoutée :</strong> Le client bénéficie d'un partenariat stratégique complet, 
                            avec une transformation mesurable, des ajustements proactifs, et une vision claire de son évolution sur plusieurs années
                          </p>
                        </div>
                        <button
                          onClick={() => setParcoursDetailModal({ isOpen: true, parcours: parcoursClientsParDuree.find(p => p.id === 's2') || null })}
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors mt-3"
                        >
                          <BookOpen className="w-4 h-4" />
                          Guide Complet
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Guide d'utilisation */}
                  <div className="bg-purple-50 rounded-lg p-6 border border-purple-200 mt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-purple-600" />
                      Comment choisir le bon parcours selon la durée ?
                    </h3>
                    <div className="space-y-4 text-sm text-gray-700">
                      <div>
                        <p className="font-bold text-orange-600 mb-2">📅 Court terme (&lt; 3 mois) :</p>
                        <p className="mb-2">Pour des besoins spécifiques, projets ponctuels, ou clients qui veulent tester avant de s'engager.</p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                          <li>Besoin rapide ? → "L'Éclaireur" (Diagnostic)</li>
                          <li>Enjeux sécurité ? → "Le Gardien" (Audit Cybersécurité)</li>
                          <li>Résultats rapides ? → "L'Accélérateur" (Quick Wins)</li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-bold text-blue-600 mb-2">📅 Tactique (1 an) :</p>
                        <p className="mb-2">Pour des clients qui veulent une roadmap claire et voir leur évolution sur 1 an, avec révisions trimestrielles.</p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                          <li>Focus roadmap ? → "Le Cartographe" (Roadmap & Évolution)</li>
                          <li>Transformation complète ? → "Le Stratège" (ODS & Transformation)</li>
                        </ul>
                        <p className="text-xs text-blue-600 mt-2 italic">
                          ✨ <strong>Valeur clé :</strong> Le client voit son évolution trimestre par trimestre, avec ajustements et progression mesurable
                        </p>
                      </div>
                      <div>
                        <p className="font-bold text-purple-600 mb-2">📅 Stratégique (2-3 ans) :</p>
                        <p className="mb-2">Pour des clients qui veulent une vision long terme, avec révision annuelle et suivi continu de l'évolution sur plusieurs années.</p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                          <li>Vision transformation ? → "Le Visionnaire" (Transformation Durable)</li>
                          <li>Partenariat complet ? → "L'Excellence" (Partenariat Stratégique)</li>
                        </ul>
                        <p className="text-xs text-purple-600 mt-2 italic">
                          ✨ <strong>Valeur clé :</strong> Le client voit son évolution année sur année, avec ajustements stratégiques et progression visible sur 2-3 ans
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-purple-200 mt-4">
                        <p className="font-bold mb-2">💡 Astuce de vente :</p>
                        <p>
                          Commencez par demander : <strong>"Sur quelle durée souhaitez-vous vous engager ?"</strong> 
                          Ensuite, présentez les parcours adaptés à cette durée. Plus la durée est longue, 
                          plus le client peut voir son évolution et sa progression mesurable.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 3: PACKAGES PRIsME */}
            {activeTab === 'packages' && (
              <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                    <Package className="w-7 h-7 text-purple-600" />
                    Packages PRIsME - Par Niveau
                  </h2>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Nos <strong>packages pré-configurés</strong> sont organisés par <strong>niveau</strong> (Stratégique, Tactique, Opérationnel, Mixte). 
                    Chaque package est un <strong>parcours complet</strong> avec livrables, montants, durée, et guide de vente. 
                    <strong> Cliquez sur "Guide Complet"</strong> pour voir tous les détails : scripts de vente, objections, valeur pour le client.
                  </p>

                  {/* SECTION 1: STRATÉGIQUE */}
                  <div className="mb-8">
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl p-4 mb-4">
                      <h3 className="text-xl font-black text-white flex items-center gap-2">
                        <Target className="w-6 h-6" />
                        STRATÉGIQUE - Vision & Gouvernance
                      </h3>
                      <p className="text-indigo-100 text-sm mt-1">
                        Packages pour entreprises qui veulent une vision stratégique et une gouvernance TI professionnelle.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {parcoursStrategiques.map((parcours) => {
                        return (
                          <div key={parcours.id} className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200 hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h3 className="text-lg font-black text-gray-900 mb-2">{parcours.nom}</h3>
                                <p className="text-gray-600 text-sm mb-3">{parcours.description}</p>
                                <div className="flex items-center gap-4 text-sm mb-2">
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4 text-indigo-600" />
                                    <span className="text-gray-600">{parcours.duree}</span>
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-indigo-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-1">Prix selon taille :</p>
                                  <div className="grid grid-cols-2 gap-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">25-30:</span>
                                      <span className="font-bold text-indigo-600">{parcours.montant['25-30']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">30-50:</span>
                                      <span className="font-bold text-indigo-600">{parcours.montant['30-50']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">50-75:</span>
                                      <span className="font-bold text-indigo-600">{parcours.montant['50-75']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">75-125:</span>
                                      <span className="font-bold text-indigo-600">{parcours.montant['75-125']}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-indigo-200 mb-3">
                              <p className="text-xs font-semibold text-gray-700 mb-2">Livrables inclus ({parcours.livrables.length}) :</p>
                              <div className="flex flex-wrap gap-1">
                                {parcours.livrables.slice(0, 3).map((livrable, idx) => (
                                  <span key={idx} className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded text-xs">
                                    {livrable}
                                  </span>
                                ))}
                                {parcours.livrables.length > 3 && (
                                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                                    +{parcours.livrables.length - 3} autres
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => setParcoursDetailModal({ isOpen: true, parcours })}
                              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                            >
                              <BookOpen className="w-4 h-4" />
                              Guide Complet
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* SECTION 2: TACTIQUE */}
                  <div className="mb-8">
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-4 mb-4">
                      <h3 className="text-xl font-black text-white flex items-center gap-2">
                        <Briefcase className="w-6 h-6" />
                        TACTIQUE - Planification & Roadmap
                      </h3>
                      <p className="text-blue-100 text-sm mt-1">
                        Packages pour entreprises qui veulent une planification tactique structurée avec roadmap et budgets.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {parcoursTactiques.map((parcours) => {
                        return (
                          <div key={parcours.id} className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h3 className="text-lg font-black text-gray-900 mb-2">{parcours.nom}</h3>
                                <p className="text-gray-600 text-sm mb-3">{parcours.description}</p>
                                <div className="flex items-center gap-4 text-sm mb-2">
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4 text-blue-600" />
                                    <span className="text-gray-600">{parcours.duree}</span>
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-blue-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-1">Prix selon taille :</p>
                                  <div className="grid grid-cols-2 gap-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">25-30:</span>
                                      <span className="font-bold text-blue-600">{parcours.montant['25-30']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">30-50:</span>
                                      <span className="font-bold text-blue-600">{parcours.montant['30-50']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">50-75:</span>
                                      <span className="font-bold text-blue-600">{parcours.montant['50-75']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">75-125:</span>
                                      <span className="font-bold text-blue-600">{parcours.montant['75-125']}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-blue-200 mb-3">
                              <p className="text-xs font-semibold text-gray-700 mb-2">Livrables inclus ({parcours.livrables.length}) :</p>
                              <div className="flex flex-wrap gap-1">
                                {parcours.livrables.slice(0, 3).map((livrable, idx) => (
                                  <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                                    {livrable}
                                  </span>
                                ))}
                                {parcours.livrables.length > 3 && (
                                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                                    +{parcours.livrables.length - 3} autres
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => setParcoursDetailModal({ isOpen: true, parcours })}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                            >
                              <BookOpen className="w-4 h-4" />
                              Guide Complet
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* SECTION 3: OPÉRATIONNEL */}
                  <div className="mb-8">
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-4 mb-4">
                      <h3 className="text-xl font-black text-white flex items-center gap-2">
                        <Zap className="w-6 h-6" />
                        OPÉRATIONNEL - Support & Monitoring
                      </h3>
                      <p className="text-emerald-100 text-sm mt-1">
                        Packages pour entreprises qui veulent améliorer leur support TI opérationnel et leur monitoring.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {parcoursOperationnels.map((parcours) => {
                        return (
                          <div key={parcours.id} className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200 hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h3 className="text-lg font-black text-gray-900 mb-2">{parcours.nom}</h3>
                                <p className="text-gray-600 text-sm mb-3">{parcours.description}</p>
                                <div className="flex items-center gap-4 text-sm mb-2">
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4 text-emerald-600" />
                                    <span className="text-gray-600">{parcours.duree}</span>
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-emerald-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-1">Prix selon taille :</p>
                                  <div className="grid grid-cols-2 gap-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">25-30:</span>
                                      <span className="font-bold text-emerald-600">{parcours.montant['25-30']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">30-50:</span>
                                      <span className="font-bold text-emerald-600">{parcours.montant['30-50']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">50-75:</span>
                                      <span className="font-bold text-emerald-600">{parcours.montant['50-75']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">75-125:</span>
                                      <span className="font-bold text-emerald-600">{parcours.montant['75-125']}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-emerald-200 mb-3">
                              <p className="text-xs font-semibold text-gray-700 mb-2">Livrables inclus ({parcours.livrables.length}) :</p>
                              <div className="flex flex-wrap gap-1">
                                {parcours.livrables.slice(0, 3).map((livrable, idx) => (
                                  <span key={idx} className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs">
                                    {livrable}
                                  </span>
                                ))}
                                {parcours.livrables.length > 3 && (
                                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                                    +{parcours.livrables.length - 3} autres
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => setParcoursDetailModal({ isOpen: true, parcours })}
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                            >
                              <BookOpen className="w-4 h-4" />
                              Guide Complet
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* SECTION 4: MIXTE */}
                  <div className="mb-8">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-4 mb-4">
                      <h3 className="text-xl font-black text-white flex items-center gap-2">
                        <Star className="w-6 h-6" />
                        MIXTE - Solutions Complètes
                      </h3>
                      <p className="text-purple-100 text-sm mt-1">
                        Packages combinant plusieurs niveaux pour une solution complète et holistique.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {parcoursMixtes.map((parcours) => {
                        return (
                          <div key={parcours.id} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h3 className="text-lg font-black text-gray-900 mb-2">{parcours.nom}</h3>
                                <p className="text-gray-600 text-sm mb-3">{parcours.description}</p>
                                <div className="flex items-center gap-4 text-sm mb-2">
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4 text-purple-600" />
                                    <span className="text-gray-600">{parcours.duree}</span>
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-purple-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-1">Prix selon taille :</p>
                                  <div className="grid grid-cols-2 gap-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">25-30:</span>
                                      <span className="font-bold text-purple-600">{parcours.montant['25-30']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">30-50:</span>
                                      <span className="font-bold text-purple-600">{parcours.montant['30-50']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">50-75:</span>
                                      <span className="font-bold text-purple-600">{parcours.montant['50-75']}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">75-125:</span>
                                      <span className="font-bold text-purple-600">{parcours.montant['75-125']}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-purple-200 mb-3">
                              <p className="text-xs font-semibold text-gray-700 mb-2">Livrables inclus ({parcours.livrables.length}) :</p>
                              <div className="flex flex-wrap gap-1">
                                {parcours.livrables.slice(0, 3).map((livrable, idx) => (
                                  <span key={idx} className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                                    {livrable}
                                  </span>
                                ))}
                                {parcours.livrables.length > 3 && (
                                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                                    +{parcours.livrables.length - 3} autres
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => setParcoursDetailModal({ isOpen: true, parcours })}
                              className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                            >
                              <BookOpen className="w-4 h-4" />
                              Guide Complet
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Guide d'utilisation */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200 mt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-indigo-600" />
                      Comment utiliser les Packages PRIsME ?
                    </h3>
                    <div className="space-y-3 text-sm text-gray-700">
                      <p>
                        <strong>💡 Pour les vendeurs :</strong> Les packages sont des <strong>parcours pré-configurés</strong> avec tout ce dont vous avez besoin pour vendre. 
                        Chaque package a un nom accrocheur, des livrables clairs, des prix par taille d'entreprise, une durée, et surtout : <strong>un guide complet</strong> avec scripts de vente et réponses aux objections.
                      </p>
                      <p>
                        <strong>💰 Prix par taille d'entreprise :</strong> Les prix sont adaptés selon le nombre d'employés. 
                        Nos clients se situent principalement dans les catégories <strong>25-30, 30-50, 50-75, et 75-125 employés</strong>. 
                        Les entreprises de <strong>125-250 employés</strong> sont moins fréquentes, et <strong>250+</strong> sont très rares. 
                        Utilisez ces prix comme référence, mais ajustez selon la complexité, le secteur d'activité, et les besoins spécifiques du client.
                      </p>
                      <p>
                        <strong>🎯 Comment choisir :</strong> Identifiez d'abord le niveau du client (Stratégique, Tactique, Opérationnel, ou Mixte), 
                        puis présentez les packages correspondants. <strong>Cliquez sur "Guide Complet"</strong> pour avoir tous les détails de vente, 
                        y compris les prix pour toutes les tailles d'entreprise.
                      </p>
                      <p>
                        <strong>📊 Différence avec "Parcours Clients" :</strong> Les "Parcours Clients" sont organisés par <strong>durée</strong> (court terme, 1 an, 2-3 ans). 
                        Les "Packages PRIsME" sont organisés par <strong>niveau</strong> (Stratégique, Tactique, Opérationnel, Mixte). 
                        Les deux approches se complètent : utilisez celle qui correspond le mieux à votre conversation avec le client.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 4: 2 APPROCHES ODS */}
            {activeTab === 'approches' && (
              <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                    <Package className="w-7 h-7 text-blue-600" />
                    Les 2 Approches pour Créer une ODS
                  </h2>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    PRIsME offre <strong>2 façons flexibles</strong> de créer une Offre de Service (ODS). 
                    Les deux approches suivent la même méthodologie : <strong>l'IA génère</strong>, puis 
                    <strong> l'expert humain approuve et ajuste</strong> selon la proposition de l'IA. 
                    C'est cette combinaison IA + Expertise humaine qui garantit une ODS professionnelle, 
                    structurée et parfaitement adaptée au client.
                  </p>

                  {/* Comparaison visuelle */}
                  <div className="grid grid-cols-2 gap-6 mb-8">
                    {/* Approche 1 */}
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-indigo-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                          1
                        </div>
                        <h3 className="text-xl font-black text-gray-900">Approche Holistique</h3>
                      </div>
                      <p className="text-gray-700 mb-4 text-sm">
                        <strong>Comment ça marche :</strong> Vous remplissez une prise de besoin complète (comme un questionnaire intelligent). 
                        L'IA analyse TOUT et génère automatiquement une ODS complète.
                      </p>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-indigo-600" />
                          <span>Prise de besoin structurée (5 phases)</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-indigo-600" />
                          <span>IA analyse tous les besoins</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-indigo-600" />
                          <span>ODS générée automatiquement par l'IA</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Users className="w-4 h-4 text-purple-600" />
                          <span><strong>Expert humain approuve et ajuste</strong> selon la proposition IA</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-indigo-600" />
                          <span>ODS finale professionnelle avec 3 offres adaptées</span>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <p className="text-xs text-gray-600 mb-2"><strong>Quand utiliser :</strong></p>
                        <ul className="text-xs text-gray-700 space-y-1">
                          <li>• Client veut une vision complète</li>
                          <li>• Premier contact, découverte</li>
                          <li>• Besoin de comprendre tous les aspects</li>
                          <li>• Temps disponible pour prise de besoin</li>
                        </ul>
                      </div>
                    </div>

                    {/* Approche 2 */}
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-emerald-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                          2
                        </div>
                        <h3 className="text-xl font-black text-gray-900">Approche par Livrables</h3>
                      </div>
                      <p className="text-gray-700 mb-4 text-sm">
                        <strong>Comment ça marche :</strong> Vous cochez les livrables tangibles que le client veut. 
                        PRIsME génère automatiquement une ODS basée sur ces livrables sélectionnés.
                      </p>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                          <span>Sélection de livrables tangibles</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                          <span>IA suggère livrables complémentaires</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                          <span>ODS générée automatiquement par l'IA</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Users className="w-4 h-4 text-purple-600" />
                          <span><strong>Expert humain approuve et ajuste</strong> selon la proposition IA</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                          <span>ODS finale professionnelle avec 3 offres adaptées</span>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-emerald-200">
                        <p className="text-xs text-gray-600 mb-2"><strong>Quand utiliser :</strong></p>
                        <ul className="text-xs text-gray-700 space-y-1">
                          <li>• Client sait ce qu'il veut</li>
                          <li>• Besoin rapide, pas de temps pour découverte</li>
                          <li>• Client orienté "produit"</li>
                          <li>• Vente par livrables tangibles</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Processus commun : IA + Expert */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-600" />
                      Le Processus Commun : IA + Expertise Humaine
                    </h3>
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <p className="text-sm text-gray-700 mb-3">
                        Peu importe l'approche choisie (Holistique ou Livrables), le processus est le même :
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                            1
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">L'IA génère une proposition</p>
                            <p className="text-xs text-gray-600">Basée sur la prise de besoin ou les livrables sélectionnés</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                            2
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">L'expert humain approuve et ajuste</p>
                            <p className="text-xs text-gray-600">Validation de la qualité, ajustements selon le contexte client, personnalisation finale</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                            3
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">ODS finale professionnelle</p>
                            <p className="text-xs text-gray-600">Document prêt à être présenté au client, avec 3 offres adaptées et tous les détails</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-purple-600 font-semibold mt-4">
                        ✨ <strong>Pourquoi c'est important :</strong> L'IA apporte la vitesse et la structure. 
                        L'expert humain apporte le jugement, le contexte, et la personnalisation. 
                        Ensemble, ils créent l'excellence.
                      </p>
                    </div>
                  </div>

                  {/* Avantages des deux approches */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-blue-600" />
                      Pourquoi les deux approches sont géniales ?
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="font-bold text-indigo-600 mb-2">Flexibilité</h4>
                        <p className="text-sm text-gray-700">
                          Vous adaptez votre approche selon le client. Certains préfèrent parler de leurs besoins, 
                          d'autres veulent voir des livrables concrets. Vous avez les deux options !
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="font-bold text-emerald-600 mb-2">Résultat identique</h4>
                        <p className="text-sm text-gray-700">
                          Peu importe l'approche, vous obtenez une ODS professionnelle avec 3 offres adaptées, 
                          spider charts, budgets, et tout le reste. La qualité est la même.
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="font-bold text-purple-600 mb-2">Gain de temps</h4>
                        <p className="text-sm text-gray-700">
                          Les deux approches sont rapides. L'approche holistique prend 30-60 minutes de prise de besoin. 
                          L'approche livrables prend 10-15 minutes de sélection. L'IA fait le reste.
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="font-bold text-orange-600 mb-2">Professionnalisme</h4>
                        <p className="text-sm text-gray-700">
                          Les deux approches génèrent des ODS conformes ITIL/COBIT, avec standards professionnels. 
                          Vous passez pour un expert, peu importe l'approche choisie.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Exemple concret */}
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Exemple concret :</h3>
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-sm text-gray-700 mb-2">
                          <strong>Scénario 1 - Approche Holistique :</strong>
                        </p>
                        <p className="text-sm text-gray-600">
                          "Monsieur le client, pour bien comprendre vos besoins, je vais vous poser quelques questions sur votre organisation, 
                          vos objectifs, votre infrastructure TI actuelle, et vos enjeux. Ça prendra environ 30 minutes. 
                          Ensuite, je vous présenterai une ODS complète avec 3 offres adaptées à votre situation."
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-sm text-gray-700 mb-2">
                          <strong>Scénario 2 - Approche Livrables :</strong>
                        </p>
                        <p className="text-sm text-gray-600">
                          "Monsieur le client, voici notre catalogue de livrables tangibles. Cochez ceux qui vous intéressent 
                          (par exemple : Plan Directeur TI, Audit sécurité, Roadmap annuel). 
                          Je vais générer une ODS avec 3 offres qui incluent ces livrables, plus d'autres recommandés par notre IA."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 4: LIVRABLES TANGIBLES */}
            {activeTab === 'livrables' && (
              <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                    <FileText className="w-7 h-7 text-emerald-600" />
                    Livrables Tangibles - Votre Catalogue de Vente
                  </h2>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Voici <strong>tous les livrables tangibles</strong> que vous pouvez proposer aux clients. 
                    Chaque livrable est associé à un <strong>standard professionnel</strong> (ITIL, COBIT, NIST, etc.) 
                    et à un <strong>niveau</strong> (Stratégique, Tactique, Opérationnel).
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                      <div className="text-2xl font-bold text-indigo-600">{livrables.length}</div>
                      <div className="text-xs text-gray-600">Livrables totaux</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="text-2xl font-bold text-blue-600">{livrablesParNiveau.strategique.length}</div>
                      <div className="text-xs text-gray-600">Stratégiques</div>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                      <div className="text-2xl font-bold text-emerald-600">{livrablesParNiveau.tactique.length}</div>
                      <div className="text-xs text-gray-600">Tactiques</div>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                      <div className="text-2xl font-bold text-teal-600">{livrablesParNiveau.operationnel.length}</div>
                      <div className="text-xs text-gray-600">Opérationnels</div>
                    </div>
                  </div>

                  {/* Filtres */}
                  <div className="flex gap-2 mb-6 flex-wrap">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold">
                      Tous ({livrables.length})
                    </button>
                    <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold">
                      Stratégique ({livrablesParNiveau.strategique.length})
                    </button>
                    <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold">
                      Tactique ({livrablesParNiveau.tactique.length})
                    </button>
                    <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold">
                      Opérationnel ({livrablesParNiveau.operationnel.length})
                    </button>
                  </div>

                  {/* Liste des livrables par pilier */}
                  <div className="space-y-6">
                    {piliers.map((pilier) => {
                      const livrablesPilier = livrables.filter(l => l.pilierId === pilier.id);
                      if (livrablesPilier.length === 0) return null;

                      return (
                        <div key={pilier.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center gap-3 mb-4">
                            <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${pilier.couleur} flex items-center justify-center text-white font-bold`}>
                              P{pilier.id}
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">{pilier.nom}</h3>
                              <p className="text-sm text-gray-600">{livrablesPilier.length} livrables disponibles</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {livrablesPilier.slice(0, 9).map((livrable) => (
                              <div key={livrable.id} className="bg-white rounded-lg p-3 border border-gray-200 hover:border-indigo-300 transition-colors">
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-gray-900">{livrable.livrableNom}</h4>
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    livrable.standard === 'ITIL' ? 'bg-blue-100 text-blue-700' :
                                    livrable.standard === 'COBIT' ? 'bg-purple-100 text-purple-700' :
                                    livrable.standard === 'NIST' ? 'bg-green-100 text-green-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {livrable.standard}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mb-2 line-clamp-2">{livrable.description}</p>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    livrable.profondeur === 'Humaine' ? 'bg-indigo-100 text-indigo-700' :
                                    livrable.profondeur === 'Procédurale' ? 'bg-blue-100 text-blue-700' :
                                    livrable.profondeur === 'Technologique' ? 'bg-emerald-100 text-emerald-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {livrable.profondeur}
                                  </span>
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    livrable.type === 'Document' ? 'bg-orange-100 text-orange-700' :
                                    livrable.type === 'Outil' ? 'bg-teal-100 text-teal-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {livrable.type}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          {livrablesPilier.length > 9 && (
                            <p className="text-sm text-gray-600 mt-4 text-center">
                              + {livrablesPilier.length - 9} autres livrables...
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Guide d'utilisation */}
                  <div className="bg-emerald-50 rounded-lg p-6 border border-emerald-200 mt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-emerald-600" />
                      Comment vendre avec les livrables ?
                    </h3>
                    <div className="space-y-3 text-sm text-gray-700">
                      <p>
                        <strong>💡 Astuce #1 :</strong> Montrez le catalogue de livrables au client. 
                        C'est visuel, concret, et ça leur donne une idée claire de ce qu'ils vont recevoir.
                      </p>
                      <p>
                        <strong>💡 Astuce #2 :</strong> Expliquez les standards (ITIL, COBIT, NIST). 
                        Ça montre votre professionnalisme et ça rassure le client.
                      </p>
                      <p>
                        <strong>💡 Astuce #3 :</strong> Utilisez l'approche par livrables pour les clients orientés "produit". 
                        Ils voient ce qu'ils achètent, c'est rassurant.
                      </p>
                      <p>
                        <strong>💡 Astuce #4 :</strong> Chaque livrable a un niveau (Stratégique/Tactique/Opérationnel). 
                        Expliquez pourquoi certains livrables sont nécessaires avant d'autres (la pyramide).
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 5: AVANTAGES VENDEURS */}
            {activeTab === 'avantages' && (
              <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                    <Star className="w-7 h-7 text-yellow-600" />
                    Avantages pour Vous - Les Vendeurs
                  </h2>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    PRIsME n'est pas juste un outil pour les clients. C'est aussi <strong>votre meilleur allié</strong> pour vendre plus, 
                    mieux, et avec confiance. Voici pourquoi PRIsME va transformer votre façon de vendre.
                  </p>

                  {/* Avantages */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Avantage 1 */}
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-indigo-600 text-white rounded-lg p-3">
                          <Rocket className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Vous passez pour un PRO</h3>
                      </div>
                      <p className="text-gray-700 text-sm mb-4">
                        Avec PRIsME, vous présentez des documents professionnels conformes ITIL/COBIT/NIST. 
                        Vous n'êtes plus "juste un vendeur", vous êtes un <strong>consultant expert</strong>.
                      </p>
                      <div className="bg-white rounded-lg p-3 border border-indigo-200">
                        <p className="text-xs text-gray-600">
                          <strong>Résultat :</strong> Le client vous respecte, vous écoute, et vous fait confiance. 
                          Les ventes se ferment plus facilement.
                        </p>
                      </div>
                    </div>

                    {/* Avantage 2 */}
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-600 text-white rounded-lg p-3">
                          <Clock className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Gain de temps ÉNORME</h3>
                      </div>
                      <p className="text-gray-700 text-sm mb-4">
                        Avant PRIsME : 2-3 jours pour créer une ODS manuelle. Avec PRIsME : <strong>30-60 minutes</strong> 
                        de prise de besoin, l'IA fait le reste. Vous avez une ODS professionnelle en quelques heures.
                      </p>
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <p className="text-xs text-gray-600">
                          <strong>Résultat :</strong> Vous servez plus de clients, vous fermez plus de deals, 
                          vous gagnez plus d'argent.
                        </p>
                      </div>
                    </div>

                    {/* Avantage 3 */}
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-emerald-600 text-white rounded-lg p-3">
                          <Zap className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Simplicité maximale</h3>
                      </div>
                      <p className="text-gray-700 text-sm mb-4">
                        Vous n'avez pas besoin d'être un expert TI pour utiliser PRIsME. 
                        Vous remplissez un questionnaire ou vous cochez des livrables. <strong>C'est tout.</strong>
                      </p>
                      <div className="bg-white rounded-lg p-3 border border-emerald-200">
                        <p className="text-xs text-gray-600">
                          <strong>Résultat :</strong> Moins de stress, plus de confiance, 
                          vous vous concentrez sur la relation client au lieu de créer des documents.
                        </p>
                      </div>
                    </div>

                    {/* Avantage 4 */}
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-orange-600 text-white rounded-lg p-3">
                          <Award className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Cohérence & Qualité</h3>
                      </div>
                      <p className="text-gray-700 text-sm mb-4">
                        Tous les vendeurs utilisent les mêmes outils, les mêmes parcours, les mêmes livrables. 
                        La qualité est <strong>toujours la même</strong>, peu importe qui vend.
                      </p>
                      <div className="bg-white rounded-lg p-3 border border-orange-200">
                        <p className="text-xs text-gray-600">
                          <strong>Résultat :</strong> Votre équipe est perçue comme professionnelle et cohérente. 
                          Les clients savent à quoi s'attendre.
                        </p>
                      </div>
                    </div>

                    {/* Avantage 5 */}
                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-pink-600 text-white rounded-lg p-3">
                          <TrendingUp className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Différenciation concurrentielle</h3>
                      </div>
                      <p className="text-gray-700 text-sm mb-4">
                        Vos concurrents présentent des offres génériques sur Word ou Excel. 
                        Vous présentez des <strong>ODS professionnelles avec spider charts, budgets détaillés, 
                        et conformité ITIL/COBIT</strong>.
                      </p>
                      <div className="bg-white rounded-lg p-3 border border-pink-200">
                        <p className="text-xs text-gray-600">
                          <strong>Résultat :</strong> Vous vous démarquez immédiatement. 
                          Le client voit la différence de qualité et choisit BZ.
                        </p>
                      </div>
                    </div>

                    {/* Avantage 6 */}
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-600 text-white rounded-lg p-3">
                          <Users className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Support & Formation</h3>
                      </div>
                      <p className="text-gray-700 text-sm mb-4">
                        Vous n'êtes pas seul. Ce guide vous accompagne, et vous avez accès à toute l'équipe PRIsME 
                        pour des questions. <strong>Vous apprenez en vendant</strong>.
                      </p>
                      <div className="bg-white rounded-lg p-3 border border-purple-200">
                        <p className="text-xs text-gray-600">
                          <strong>Résultat :</strong> Vous devenez meilleur chaque jour. 
                          Votre expertise grandit, votre confiance aussi.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Message motivationnel */}
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white mt-6">
                    <h3 className="text-2xl font-black mb-4">Vous allez devenir l'excellence</h3>
                    <p className="text-lg text-indigo-100 mb-4">
                      Avec PRIsME, vous ne vendez plus juste des services TI. Vous vendez de la <strong>transformation</strong>, 
                      de la <strong>vision</strong>, de l'<strong>excellence</strong>.
                    </p>
                    <p className="text-base text-indigo-200">
                      Vos clients vont vous voir comme un partenaire stratégique, pas comme un fournisseur. 
                      C'est ça, la différence PRIsME. C'est ça, votre avantage concurrentiel.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 6: SCRIPTS DE VENTE */}
            {activeTab === 'scripts' && (
              <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                    <Presentation className="w-7 h-7 text-orange-600" />
                    Scripts de Vente & Réponses aux Objections
                  </h2>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Voici des <strong>scripts prêts à l'emploi</strong> et des réponses aux objections courantes. 
                    Adaptez-les selon votre style et le contexte du client.
                  </p>

                  {/* Scripts d'ouverture */}
                  <div className="space-y-6">
                    <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Target className="w-5 h-5 text-indigo-600" />
                        Script d'Ouverture - Présentation PRIsME
                      </h3>
                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          "Bonjour [Nom du client]. Je suis [Votre nom] de BZ. Aujourd'hui, je vais vous présenter 
                          <strong> PRIsME</strong>, notre plateforme qui transforme la façon dont nous livrons des solutions TI.
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed mt-3">
                          PRIsME, c'est comme avoir un <strong>consultant expert TI</strong> qui travaille avec vous 24/7. 
                          On commence par comprendre vos besoins avec une prise de besoin structurée, puis notre IA génère 
                          automatiquement des documents professionnels : Plan Directeur TI, Roadmap, ODS, Budgets, tout ça 
                          conforme aux standards ITIL, COBIT et NIST.
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed mt-3">
                          L'avantage pour vous ? Vous avez une <strong>vision claire</strong> de où vous êtes, où vous voulez aller, 
                          et comment y arriver. Pour moi ? Je peux vous servir mieux, plus rapidement, et avec une qualité 
                          professionnelle constante.
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed mt-3">
                          Est-ce que ça vous intéresse de voir comment ça fonctionne concrètement ?"
                        </p>
                      </div>
                    </div>

                    {/* Objections courantes */}
                    <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        Réponses aux Objections Courantes
                      </h3>

                      {/* Objection 1 */}
                      <div className="bg-white rounded-lg p-4 border border-red-200 mb-4">
                        <h4 className="font-bold text-red-600 mb-2">Objection : "C'est trop cher"</h4>
                        <div className="text-sm text-gray-700 space-y-2">
                          <p><strong>Réponse :</strong></p>
                          <p>
                            "Je comprends votre préoccupation. Regardons la valeur plutôt que le prix. 
                            Avec PRIsME, vous obtenez des documents professionnels qui coûteraient normalement 
                            <strong> 50K-100K$</strong> en consultant externe. Nous, on vous les livre pour une fraction du prix, 
                            et en quelques jours au lieu de plusieurs mois.
                          </p>
                          <p>
                            De plus, ces documents vous font économiser du temps, évitent des erreurs coûteuses, 
                            et vous donnent une roadmap claire. Le ROI est là. Voulez-vous qu'on regarde ensemble 
                            combien vous pourriez économiser avec une meilleure planification TI ?"
                          </p>
                        </div>
                      </div>

                      {/* Objection 2 */}
                      <div className="bg-white rounded-lg p-4 border border-red-200 mb-4">
                        <h4 className="font-bold text-red-600 mb-2">Objection : "On n'a pas besoin de ça, on gère bien nos TI"</h4>
                        <div className="text-sm text-gray-700 space-y-2">
                          <p><strong>Réponse :</strong></p>
                          <p>
                            "C'est excellent que vous gérez bien vos TI ! Ça veut dire que vous avez déjà une base solide. 
                            Mais permettez-moi de vous poser une question : avez-vous une <strong>vision stratégique</strong> 
                            de vos TI sur 3-5 ans ? Avez-vous un plan d'action structuré pour les 12-24 prochains mois ?
                          </p>
                          <p>
                            PRIsME ne remplace pas ce que vous faites déjà. On <strong>complète</strong> et on <strong>structure</strong> 
                            ce que vous avez. C'est comme avoir les fondations d'une maison (votre opérationnel) et ajouter 
                            les murs et le toit (stratégie et tactique). Ça rend votre maison complète et solide.
                          </p>
                          <p>
                            Voulez-vous qu'on fasse un diagnostic rapide pour voir où vous êtes et ce qui pourrait être amélioré ?"
                          </p>
                        </div>
                      </div>

                      {/* Objection 3 */}
                      <div className="bg-white rounded-lg p-4 border border-red-200 mb-4">
                        <h4 className="font-bold text-red-600 mb-2">Objection : "On va y penser"</h4>
                        <div className="text-sm text-gray-700 space-y-2">
                          <p><strong>Réponse :</strong></p>
                          <p>
                            "Parfait, je comprends que vous voulez prendre le temps de réfléchir. C'est une décision importante. 
                            Pour vous aider dans votre réflexion, j'aimerais vous proposer quelque chose :
                          </p>
                          <p>
                            On peut faire un <strong>diagnostic TI rapide</strong> (gratuit, 30 minutes). 
                            Ça vous donne une idée concrète de votre posture actuelle et des opportunités d'amélioration. 
                            Ensuite, vous décidez si vous voulez aller plus loin.
                          </p>
                          <p>
                            Ça vous convient ? On peut le faire cette semaine ou la semaine prochaine ?"
                          </p>
                        </div>
                      </div>

                      {/* Objection 4 */}
                      <div className="bg-white rounded-lg p-4 border border-red-200">
                        <h4 className="font-bold text-red-600 mb-2">Objection : "On a déjà un fournisseur TI"</h4>
                        <div className="text-sm text-gray-700 space-y-2">
                          <p><strong>Réponse :</strong></p>
                          <p>
                            "C'est parfait ! Avoir un fournisseur TI, c'est essentiel. PRIsME ne remplace pas votre fournisseur, 
                            on <strong>complète</strong> ce qu'il fait.
                          </p>
                          <p>
                            Votre fournisseur gère probablement le support quotidien, les incidents, les changements. 
                            C'est le niveau <strong>opérationnel</strong>. PRIsME ajoute les niveaux <strong>stratégique</strong> 
                            et <strong>tactique</strong> : la vision à long terme, la planification, la gouvernance.
                          </p>
                          <p>
                            C'est comme avoir un médecin de famille (votre fournisseur) ET un spécialiste en santé préventive (PRIsME). 
                            Les deux sont complémentaires. Voulez-vous qu'on regarde comment PRIsME pourrait améliorer votre relation 
                            avec votre fournisseur actuel ?"
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Techniques de closing */}
                    <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        Techniques de Closing
                      </h3>
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <h4 className="font-bold text-green-600 mb-2">Closing Assumptif</h4>
                          <p className="text-sm text-gray-700">
                            "Parfait ! Pour commencer, je vais vous préparer une ODS avec 3 offres adaptées à votre budget. 
                            Vous préférez qu'on commence par quel parcours ? Le Parcours B (Stratégie & Roadmap) serait idéal pour vous."
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <h4 className="font-bold text-green-600 mb-2">Closing par Choix</h4>
                          <p className="text-sm text-gray-700">
                            "Vous avez deux options : on peut commencer par un diagnostic TI rapide (5K$) pour comprendre votre posture, 
                            ou directement par un Plan Directeur complet (25K$). Quelle option vous convient le mieux ?"
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <h4 className="font-bold text-green-600 mb-2">Closing par Urgence</h4>
                          <p className="text-sm text-gray-700">
                            "Je comprends que vous voulez réfléchir. Par contre, si on commence maintenant, je peux vous garantir 
                            une ODS complète d'ici 2 semaines. Si on attend, ça pourrait prendre 4-6 semaines à cause de la charge. 
                            Qu'est-ce qui vous convient le mieux ?"
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 7: COMPARAISON AVANT/APRÈS */}
            {activeTab === 'comparaison' && (
              <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                    <BarChart3 className="w-7 h-7 text-purple-600" />
                    Avant PRIsME vs Après PRIsME
                  </h2>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Voici une comparaison concrète de ce que c'était <strong>avant PRIsME</strong> et ce que c'est 
                    <strong> maintenant avec PRIsME</strong>. Utilisez cette comparaison pour montrer la valeur aux clients.
                  </p>

                  {/* Tableau comparatif */}
                  <div className="overflow-x-auto mb-6">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Aspect</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-bold text-red-600">Avant PRIsME</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-bold text-green-600">Avec PRIsME</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Temps pour créer une ODS</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">2-3 jours manuels</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">30-60 minutes (prise de besoin)</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Qualité des documents</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">Variable, dépend du vendeur</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">Toujours professionnelle, conforme ITIL/COBIT</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Cohérence entre vendeurs</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">Chacun fait à sa façon</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">Tous utilisent les mêmes outils et standards</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Visualisations (spider charts, graphiques)</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">Manuelles, longues à créer</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">Générées automatiquement par l'IA</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Standards professionnels</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">Parfois mentionnés, pas toujours appliqués</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">Toujours conformes ITIL 4, COBIT, NIST</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Personnalisation selon le client</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">Manuelle, prend du temps</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">Automatique, basée sur la prise de besoin</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Approche flexible</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">Une seule façon de faire</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">2 approches : Holistique OU par Livrables</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Perception du client</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">"C'est un vendeur"</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">"C'est un consultant expert"</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Différenciation concurrentielle</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">Offres génériques similaires aux concurrents</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">ODS professionnelles qui se démarquent</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-700">Taux de conversion</td>
                          <td className="border border-gray-300 px-4 py-3 text-gray-600">Variable, dépend de plusieurs facteurs</td>
                          <td className="border border-gray-300 px-4 py-3 text-green-700 font-semibold">Amélioré grâce à la qualité et professionnalisme</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Message final */}
                  <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-8 text-white">
                    <h3 className="text-2xl font-black mb-4">La différence est évidente</h3>
                    <p className="text-lg text-green-100 mb-4">
                      Avec PRIsME, vous ne vendez plus comme avant. Vous vendez avec <strong>excellence</strong>, 
                      <strong> professionnalisme</strong>, et <strong>confiance</strong>.
                    </p>
                    <p className="text-base text-green-200">
                      Vos clients voient la différence. Vos concurrents aussi. Vous êtes maintenant dans une 
                      <strong> ligue supérieure</strong>. C'est ça, PRIsME.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Modal détaillée pour chaque niveau */}
      {niveauDetailModal.niveau && (
        <NiveauDetailModal
          isOpen={niveauDetailModal.isOpen}
          onClose={() => setNiveauDetailModal({ isOpen: false, niveau: null })}
          niveau={niveauDetailModal.niveau}
        />
      )}

      {/* Modal détaillée pour chaque parcours */}
      {parcoursDetailModal.parcours && (
        <ParcoursDetailModal
          isOpen={parcoursDetailModal.isOpen}
          onClose={() => setParcoursDetailModal({ isOpen: false, parcours: null })}
          parcours={parcoursDetailModal.parcours}
        />
      )}
    </AnimatePresence>
  );
};

export default GuideVentePRIsMEModal;

